/* eslint-disable react-hooks/exhaustive-deps */
import React,{useState,useEffect,useCallback,useRef}from'react';
import{useNavigate,useLocation}from'react-router-dom';
import Layout from'../components/layout/Layout';
import PatientSearchInput from'../components/patient/PatientSearchInput';
import api from'../services/api';
import {getCurrencySymbol} from '../utils/locale';
import {useTranslation} from 'react-i18next';
import {getStatusLabel} from '../utils/statusLabels';
import{fmtDate,fmtCurrency}from'../utils/helpers';
import{getActiveBranchId}from'../utils/branch';
import{getHospitalInfo}from'../utils/printUtils';

const INP='w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400';
const LBL='block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5';
const BTN_PRI='bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2.5 px-5 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 disabled:cursor-not-allowed';
const BTN_SEC='bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2.5 px-5 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50';
const CARD='bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden';
const CARD_HDR='px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white flex items-center justify-between';

const STATUS_CSS:Record<string,string>={
  DRAFT:'bg-slate-100 text-slate-500',
  UNPAID:'bg-red-100 text-red-700',
  PARTIAL:'bg-amber-100 text-amber-700',
  PAID:'bg-emerald-100 text-emerald-700',
  CANCELLED:'bg-slate-100 text-slate-400',
  REFUNDED:'bg-purple-100 text-purple-700',
};
const CLAIM_STATUS_CSS:Record<string,string>={
  DRAFT:'bg-slate-100 text-slate-500',
  SUBMITTED:'bg-blue-100 text-blue-700',
  UNDER_REVIEW:'bg-amber-100 text-amber-700',
  APPROVED:'bg-emerald-100 text-emerald-700',
  PARTIALLY_APPROVED:'bg-teal-100 text-teal-700',
  REJECTED:'bg-red-100 text-red-700',
  SETTLED:'bg-purple-100 text-purple-700',
};
const BILL_TYPES=['OPD','IPD','LAB','PHARMACY','RADIOLOGY','OT','PACKAGE','ADVANCE','BLOOD_BANK'];
const ITEM_CATEGORIES=['CONSULTATION','PROCEDURE','INVESTIGATION','PHARMACY','NURSING','ROOM','BED_CHARGES','BLOOD_BANK','OT','PACKAGE','DIET','OTHER'];
const PAYMENT_METHODS=['CASH','CARD','MPESA','BANK_TRANSFER','CHEQUE','INSURANCE','TPA','WALLET'];
const CLAIM_TYPES=['SHA','NHIF','TPA','CORPORATE','PRIVATE_INSURANCE'];

const BASE=process.env.REACT_APP_API_URL;
const hdr=()=>({'Content-Type':'application/json','Authorization':`Bearer ${localStorage.getItem('accessToken')||''}`});
const fmtD=(d:string)=>d?new Date(d).toLocaleDateString('en-KE',{day:'2-digit',month:'short',year:'numeric'}):'—';
const fmtDT=(d:string)=>d?new Date(d).toLocaleString('en-KE',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'}):'—';

const Field=({label,required,col2,children}:any)=>(
  <div className={col2?'col-span-2':''}>
    <label className={LBL}>{label}{required&&<span className="text-red-500 ml-0.5">*</span>}</label>
    {children}
  </div>
);
const Modal=({open,onClose,title,children,size='md'}:any)=>{
  if(!open)return null;
  const w={sm:'max-w-sm',md:'max-w-lg',lg:'max-w-2xl',xl:'max-w-4xl'}[size]||'max-w-lg';
  return(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose}/>
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full ${w} max-h-[90vh] flex flex-col overflow-hidden`}>
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-[#02357A] to-[#015CA6] flex-shrink-0">
          <h3 className="font-bold text-white text-lg">{title}</h3>
          <button onClick={onClose} className="text-white/70 hover:text-white text-xl font-bold">✕</button>
        </div>
        <div className="p-6 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
};

// ── Admission Search ──────────────────────────────────────────────────────────
function AdmissionSearchInput({onSelect}:{onSelect:(a:any)=>void}){
  const[q,setQ]=useState('');
  const[results,setResults]=useState<any[]>([]);
  const[loading,setLoading]=useState(false);
  const[show,setShow]=useState(false);
  const[recent,setRecent]=useState<any[]>([]);
  const ref=useRef<HTMLDivElement>(null);

  useEffect(()=>{
    api.getAdmissions?.({status:'ADMITTED',take:8}).then((r:any)=>{
      const list=r?.data?.admissions||r?.data?.data||(Array.isArray(r?.data)?r.data:[]);
      setRecent(list);
    }).catch(()=>{});
  },[]);

  useEffect(()=>{
    const h=(e:MouseEvent)=>{if(ref.current&&!ref.current.contains(e.target as Node))setShow(false);};
    document.addEventListener('mousedown',h);return()=>document.removeEventListener('mousedown',h);
  },[]);

  useEffect(()=>{
    if(q.length<2){setResults([]);return;}
    const t=setTimeout(async()=>{
      setLoading(true);
      try{const r=await api.getAdmissions?.({search:q,take:10});
        setResults(r?.data?.admissions||r?.data?.data||(Array.isArray(r?.data)?r.data:[]));setShow(true);
      }catch{setResults([]);}
      setLoading(false);
    },300);
    return()=>clearTimeout(t);
  },[q]);

  const pick=(a:any)=>{setQ('');setResults([]);setShow(false);onSelect(a);};
  const display=q.length>=2?results:recent;

  return(
    <div className={CARD}>
      <div className={CARD_HDR}><h3 className="font-bold text-slate-800">🏥 Search IPD Admission</h3></div>
      <div className="p-5">
        <div ref={ref} className="relative">
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
            <input value={q} onChange={e=>setQ(e.target.value)} onFocus={()=>setShow(true)}
              placeholder="Search by patient name, UHID, IPD number..."
              className={`${INP} pl-10 pr-10`}/>
            {loading&&<div className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-[#02357A]/20 border-t-[#02357A] rounded-full animate-spin"/>}
            {!loading&&q&&<button onClick={()=>{setQ('');setResults([]);}} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-600 text-xl">×</button>}
          </div>
          {show&&(
            <div className=" z-[9999] top-full left-0 right-0 mt-1.5 bg-white border-2 border-slate-200 rounded-2xl shadow-2xl overflow-y-auto max-h-72" style={{boxShadow:'0 25px 50px -12px rgba(0,0,0,0.35)'}}>
              <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{q.length<2?'🕐 Recent Admissions':'🔍 Results'}</span>
                <span className="text-xs text-slate-300">{q.length<2?'Type to search':display.length+' found'}</span>
              </div>
              {display.length>0?display.map((a:any)=>{
                const pname=`${a.patientFirstName||a.patient_first_name||a.firstName||''} ${a.patientLastName||a.patient_last_name||a.lastName||''}`.trim();
                return(
                  <div key={a.id} onClick={()=>pick(a)} className="flex items-center gap-3 px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-slate-100 last:border-0 transition-colors group">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#02357A] to-[#039089] flex items-center justify-center text-white font-black text-sm flex-shrink-0">{(pname||'P')[0].toUpperCase()}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-slate-800 text-sm group-hover:text-[#02357A]">{pname||'Unknown'}</p>
                        <span className="font-mono text-xs text-[#02357A] bg-blue-50 px-2 py-0.5 rounded-lg border border-blue-100">{a.ipdNumber||a.ipd_number||'—'}</span>
                      </div>
                      <div className="flex gap-2 text-xs text-slate-400 mt-0.5 flex-wrap">
                        <span>UHID: {a.uhid||'—'}</span><span>·</span>
                        <span>Ward: {a.wardName||a.ward_name||'—'}</span><span>·</span>
                        <span>Bed: {a.bedNumber||a.bed_number||'—'}</span>
                        {(a.admissionDate||a.admission_date)&&<><span>·</span><span>{fmtD(a.admissionDate||a.admission_date)}</span></>}
                      </div>
                    </div>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-bold flex-shrink-0 ${a.status==='ADMITTED'?'bg-emerald-100 text-emerald-700':'bg-slate-100 text-slate-500'}`}>{a.status||'ADMITTED'}</span>
                  </div>
                );
              }):(q.length>=2&&!loading&&<div className="p-6 text-center"><p className="text-sm text-slate-400">No admissions found for "{q}"</p></div>)}
            </div>
          )}
        </div>
        <p className="text-xs text-slate-400 mt-2">Showing admitted patients · Select to load billing summary</p>
      </div>
    </div>
  );
}

const DEF_FORM={
  patientId:'',billType:'OPD',admissionId:'',notes:'',
  discountAmount:0,discountReason:'',discountType:'FIXED',
  gstPercentage:0,insuranceClaim:false,
  tpaId:'',tpaApprovalNumber:'',tpaAmount:0,dueDate:'',
  items:[{description:'',serviceId:'',itemCode:'',quantity:1,unitPrice:0,taxPercentage:0,itemCategory:'CONSULTATION'}],
};

export default function BillingPage(){
  const {t}=useTranslation();
  const nav=useNavigate();
  const loc=useLocation();
  const[tab,setTab]=useState('bills');
  const[bills,setBills]=useState<any[]>([]);
  const[loading,setLoading]=useState(false);
  const[saving,setSaving]=useState(false);
  const[msg,setMsg]=useState('');
  const[search,setSearch]=useState('');
  const[statusFilter,setStatusFilter]=useState('');
  const[typeFilter,setTypeFilter]=useState('');
  const[dateFrom,setDateFrom]=useState('');
  const[dateTo,setDateTo]=useState('');
  const[selectedPatient,setSelectedPatient]=useState<any>(null);
  const[billForm,setBillForm]=useState<any>({...DEF_FORM});
  const[stats,setStats]=useState<any>({});
  const[serviceMaster,setServiceMaster]=useState<any[]>([]);
  const[packages,setPackages]=useState<any[]>([]);
  const[serviceSearch,setServiceSearch]=useState('');
  const[selBill,setSelBill]=useState<any>(null);
  const[selBillItems,setSelBillItems]=useState<any[]>([]);
  const[selBillPayments,setSelBillPayments]=useState<any[]>([]);
  const[selBillDeposits,setSelBillDeposits]=useState<any[]>([]);
  const[selBillClaims,setSelBillClaims]=useState<any[]>([]);
  // IPD billing
  const[ipdAdmissionId,setIpdAdmissionId]=useState('');
  const[ipdPatientId,setIpdPatientId]=useState('');
  const[ipdNumber,setIpdNumber]=useState('');
  const[ipdSummary,setIpdSummary]=useState<any>(null);
  const[ipdCharges,setIpdCharges]=useState<any[]>([]);
  const[ipdDeposits,setIpdDeposits]=useState<any[]>([]);
  const[ipdBillLoading,setIpdBillLoading]=useState(false);
  const[ipdExistingBill,setIpdExistingBill]=useState<any>(null);
  const[ipdOrders,setIpdOrders]=useState<any[]>([]);
  const[ipdNotes,setIpdNotes]=useState<any[]>([]);
  const[ipdRounds,setIpdRounds]=useState<any[]>([]);
  const[ipdAdmDetail,setIpdAdmDetail]=useState<any>(null);
  const[ipdActiveSection,setIpdActiveSection]=useState('billing');
  // Cash counter
  const[shift,setShift]=useState<any>(null);
  // Insurance claims
  const[allClaims,setAllClaims]=useState<any[]>([]);
  // Modals
  const[showPayModal,setShowPayModal]=useState(false);
  const[showDiscModal,setShowDiscModal]=useState(false);
  const[showRefundModal,setShowRefundModal]=useState(false);
  const[showDepositModal,setShowDepositModal]=useState(false);
  const[showClaimModal,setShowClaimModal]=useState(false);
  const[showShiftModal,setShowShiftModal]=useState(false);
  const[showAddItemModal,setShowAddItemModal]=useState(false);
  const[payForm,setPayForm]=useState({amount:'',paymentMethod:'CASH',transactionId:'',remarks:''});
  const[discForm,setDiscForm]=useState({discountType:'FIXED',fixedAmount:'',percentage:'',reason:''});
  const[refundForm,setRefundForm]=useState({refundAmount:'',refundMethod:'CASH',refundReason:''});
  const[depositForm,setDepositForm]=useState({amount:'',paymentMode:'CASH',referenceNumber:'',notes:''});
  const[claimForm,setClaimForm]=useState({claimType:'TPA',insuranceCompany:'',policyNumber:'',authorizationNumber:'',claimAmount:'',patientShare:'',remarks:''});
  const[shiftForm,setShiftForm]=useState({counterName:'',openingBalance:''});
  const[addItemForm,setAddItemForm]=useState({description:'',serviceId:'',itemCode:'',itemCategory:'CONSULTATION',quantity:1,unitPrice:0,taxPercentage:0});
  const[addItemSearch,setAddItemSearch]=useState('');

  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),4000);};

  // ── Load bills ──────────────────────────────────────────────────────────────
  const loadBills=useCallback(async()=>{
    setLoading(true);
    try{
      const r=await api.getBills?.({status:statusFilter||undefined,billType:typeFilter||undefined,search:search||undefined,take:100,skip:0}).catch(()=>({data:{}}));
      setBills(r?.data?.bills||r?.data?.data||(Array.isArray(r?.data)?r.data:[]));
    }catch{}
    setLoading(false);
  },[statusFilter,typeFilter,search]);

  useEffect(()=>{if(tab==='bills')loadBills();},[tab,loadBills]);

  // ── Load stats ──────────────────────────────────────────────────────────────
  useEffect(()=>{
    if(tab==='reports'){
      api.getBillingStats?.().then((r:any)=>setStats(r?.data||{})).catch(()=>{});
    }
  },[tab]);

  // ── Load claims tab ─────────────────────────────────────────────────────────
  useEffect(()=>{
    if(tab==='claims'){
      api.getBillingInsuranceClaims?.().then((r:any)=>{
        const list=r?.data?.claims||r?.data?.data||(Array.isArray(r?.data)?r.data:[]);
        setAllClaims(list);
      }).catch(()=>{});
    }
  },[tab]);

  // ── Load cash shift ─────────────────────────────────────────────────────────
  useEffect(()=>{
    if(tab==='counter'){
      api.getCurrentShift?.().then((r:any)=>setShift(r?.data||null)).catch(()=>setShift(null));
    }
  },[tab]);

  // ── Load service master once ────────────────────────────────────────────────
  useEffect(()=>{
    if((tab==='new'||tab==='view'||tab==='ipd')&&serviceMaster.length===0){
      Promise.all([
        fetch(`${BASE}/billing/services?take=500`,{headers:hdr()}).then(r=>r.json()).catch(()=>[]),
        api.getBillingPackages?.().catch(()=>({data:[]})),
      ]).then(([svcs,pkgs])=>{
        setServiceMaster(Array.isArray(svcs)?svcs:svcs?.data||svcs?.services||[]);
        setPackages(Array.isArray(pkgs?.data)?pkgs.data:pkgs?.data?.packages||[]);
      });
    }
  },[tab]);

  // ── Navigation state ────────────────────────────────────────────────────────
  useEffect(()=>{
    const st=(loc.state as any);
    if(st?.search){setSearch(st.search);setTab('bills');}
    if(st?.newBill){setTab('new');}
    if(st?.ipdBill){
      const{admissionId,patientId,ipdNumber:ipn}=st.ipdBill;
      setTab('ipd');setIpdAdmissionId(admissionId);
      setIpdPatientId(patientId);setIpdNumber(ipn||'');
      loadIPDBill(admissionId);
    }
  },[loc.state]);

  // ── Load single bill ────────────────────────────────────────────────────────
  const loadBill=useCallback(async(id:string)=>{
    const r=await api.getBillById?.(id).catch(()=>null);
    if(!r?.data)return;
    const bill=r.data?.bill||r.data;
    setSelBill(bill);
    // Load service master for SHA enrichment
    let svcMaster:any[]=serviceMaster;
    if(svcMaster.length===0){
      const svcs=await fetch(`${BASE}/billing/services?take=500`,{headers:hdr()}).then(r=>r.json()).catch(()=>[]);
      svcMaster=Array.isArray(svcs)?svcs:svcs?.data||svcs?.services||[];
      if(svcMaster.length>0)setServiceMaster(svcMaster);
    }
    // Enrich items with SHA data — match by serviceId, itemCode, or name
    const rawItems=r.data?.items||[];
    const enriched=rawItems.map((it:any)=>{
      const sid=it.serviceId||it.service_id;
      const icode=(it.itemCode||it.item_code||'').toLowerCase();
      const iname=(it.itemDescription||it.item_description||'').toLowerCase();
      const svc=svcMaster.find((s:any)=>
        (sid&&s.id===sid)||
        (icode&&icode===(s.serviceCode||s.service_code||'').toLowerCase())||
        (iname&&(s.serviceName||s.service_name||'').toLowerCase()===iname)
      );
      if(svc){
        const isSHA=!!(svc.isSHACovered||svc.is_sha_covered);
        const shaRate=+(svc.shaApprovedRate||svc.sha_approved_rate||0);
        return{...it,isSHACovered:isSHA,is_sha_covered:isSHA,
          paymentMethod:isSHA?'SHA':'CASH',payment_method:isSHA?'SHA':'CASH',
          shaInterventionCode:svc.shaInterventionCode||svc.sha_intervention_code||null,
          sha_intervention_code:svc.shaInterventionCode||svc.sha_intervention_code||null,
          shaApprovedRate:shaRate||null,sha_approved_rate:shaRate||null,
          requiresPreAuth:svc.requiresPreAuth||svc.requires_pre_auth||false,
        };
      }
      return it;
    });
    setSelBillItems(enriched);
    setSelBillPayments(r.data?.payments||[]);
    // Load claims for this bill
    const cr=await api.getBillClaims?.(id).catch(()=>null);
    setSelBillClaims(Array.isArray(cr?.data)?cr.data:cr?.data?.claims||[]);
    // Load deposits if IPD
    if(bill.admissionId||bill.admission_id){
      const aid=bill.admissionId||bill.admission_id;
      const dr=await api.getIPDDeposits?.(aid).catch(()=>null);
      setSelBillDeposits(Array.isArray(dr?.data)?dr.data:dr?.data?.deposits||[]);
    }
  },[]);

  // ── IPD billing ─────────────────────────────────────────────────────────────
  const loadIPDBill=useCallback(async(admissionId:string)=>{
    setIpdBillLoading(true);
    try{
      const[summary,charges,deposits,orders,notes,rounds,admDetail]=await Promise.all([
        api.getIPDBillingSummary(admissionId).catch(()=>({data:null})),
        api.getIPDCharges(admissionId).catch(()=>({data:[]})),
        api.getIPDDeposits(admissionId).catch(()=>({data:[]})),
        api.getDoctorOrders(admissionId).catch(()=>({data:[]})),
        api.getClinicalNotes(admissionId).catch(()=>({data:[]})),
        fetch(`${BASE}/ipd/rounds/${admissionId}`,{headers:hdr()}).then(r=>r.json()).catch(()=>[]),
        api.getAdmission(admissionId).catch(()=>({data:null})),
      ]);
      setIpdSummary(summary?.data||null);
      setIpdCharges(Array.isArray(charges?.data)?charges.data:charges?.data?.charges||[]);
      const deps=Array.isArray(deposits?.data)?deposits.data:deposits?.data?.deposits||[];
      setIpdDeposits(deps);
      setIpdOrders(Array.isArray(orders?.data)?orders.data:orders?.data?.orders||[]);
      setIpdNotes(Array.isArray(notes?.data)?notes.data:notes?.data?.notes||[]);
      setIpdRounds(Array.isArray(rounds)?rounds:rounds?.data||[]);
      setIpdAdmDetail(admDetail?.data||null);
      const billId=summary?.data?.billId||summary?.data?.bill_id;
      if(billId){
        const bill=await api.getBillById?.(billId).catch(()=>null);
        if(bill?.data){
          const b=bill.data?.bill||bill.data;
          setIpdExistingBill(b);setSelBill(b);
          setSelBillItems(bill.data?.items||[]);setSelBillPayments(bill.data?.payments||[]);
          setSelBillDeposits(deps);
        }
      }else{setIpdExistingBill(null);}
    }catch{}
    setIpdBillLoading(false);
  },[]);

  // ── New bill helpers ────────────────────────────────────────────────────────
  const addItem=()=>setBillForm((f:any)=>({...f,items:[...f.items,{description:'',serviceId:'',itemCode:'',quantity:1,unitPrice:0,taxPercentage:0,itemCategory:'CONSULTATION'}]}));
  const removeItem=(i:number)=>setBillForm((f:any)=>({...f,items:f.items.filter((_:any,idx:number)=>idx!==i)}));
  const updItem=(i:number,k:string,v:any)=>setBillForm((f:any)=>({...f,items:f.items.map((it:any,idx:number)=>idx===i?{...it,[k]:v}:it)}));

  const subTotal=billForm.items.reduce((s:number,it:any)=>s+(+(it.unitPrice||0))*(+(it.quantity||1)),0);
  const gstAmt=Math.round(subTotal*(+(billForm.gstPercentage||0)/100)*100)/100;
  const discAmt=billForm.discountType==='PERCENTAGE'?Math.round(subTotal*(+(billForm.discountAmount||0)/100)*100)/100:+(billForm.discountAmount||0);
  const netTotal=Math.max(0,subTotal+gstAmt-discAmt);

  const createBill=async()=>{
    if(!billForm.patientId){notify('⚠️ Select a patient');return;}
    if(!billForm.items[0].description){notify('⚠️ Add at least one item');return;}
    setSaving(true);
    try{
      const r=await api.createBill({
        branchId:getActiveBranchId()||undefined,
        patientId:billForm.patientId,billType:billForm.billType,
        admissionId:billForm.admissionId||null,notes:billForm.notes||null,
        billDate:new Date().toISOString(),
        gstPercentage:+(billForm.gstPercentage||0),gstAmount:gstAmt,
        discountAmount:discAmt,discountReason:billForm.discountReason||null,discountType:billForm.discountType,
        totalAmount:subTotal,netAmount:netTotal,balanceAmount:netTotal,
        insuranceClaim:billForm.insuranceClaim,
        tpaId:billForm.tpaId||null,tpaApprovalNumber:billForm.tpaApprovalNumber||null,tpaAmount:+(billForm.tpaAmount||0),
        dueDate:billForm.dueDate||null,
        items:billForm.items.map((it:any)=>({
          itemDescription:it.description,serviceId:it.serviceId||null,itemCode:it.itemCode||null,
          itemCategory:it.itemCategory||'CONSULTATION',quantity:+(it.quantity||1),unitPrice:+(it.unitPrice||0),
          totalPrice:(+(it.unitPrice||0))*(+(it.quantity||1)),taxPercentage:+(it.taxPercentage||0),
          cgstAmount:Math.round((+(it.unitPrice||0))*(+(it.quantity||1))*(+(it.taxPercentage||0)/2/100)*100)/100,
          sgstAmount:Math.round((+(it.unitPrice||0))*(+(it.quantity||1))*(+(it.taxPercentage||0)/2/100)*100)/100,
        })),
      });
      notify('✅ Bill created!');
      setBillForm({...DEF_FORM});setSelectedPatient(null);
      if(r?.data?.id){await loadBill(r.data.id);setTab('view');}else setTab('bills');
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error creating bill'));}
    setSaving(false);
  };

  // ── Add item to existing bill ───────────────────────────────────────────────
  const addItemToBill=async()=>{
    if(!selBill||!addItemForm.description){notify('⚠️ Enter item description');return;}
    setSaving(true);
    try{
      await api.addBillItem?.(selBill.id,{
        itemDescription:addItemForm.description,serviceId:addItemForm.serviceId||null,
        itemCode:addItemForm.itemCode||null,itemCategory:addItemForm.itemCategory,
        quantity:+(addItemForm.quantity||1),unitPrice:+(addItemForm.unitPrice||0),
        totalPrice:(+(addItemForm.unitPrice||0))*(+(addItemForm.quantity||1)),
        taxPercentage:+(addItemForm.taxPercentage||0),
        cgstAmount:Math.round((+(addItemForm.unitPrice||0))*(+(addItemForm.quantity||1))*(+(addItemForm.taxPercentage||0)/2/100)*100)/100,
        sgstAmount:Math.round((+(addItemForm.unitPrice||0))*(+(addItemForm.quantity||1))*(+(addItemForm.taxPercentage||0)/2/100)*100)/100,
      });
      notify('✅ Item added!');setShowAddItemModal(false);
      setAddItemForm({description:'',serviceId:'',itemCode:'',itemCategory:'CONSULTATION',quantity:1,unitPrice:0,taxPercentage:0});
      setAddItemSearch('');await loadBill(selBill.id);
    }catch{notify('❌ Failed to add item');}
    setSaving(false);
  };

  const removeItemFromBill=async(itemId:string)=>{
    if(!selBill||!window.confirm('Remove this item?'))return;
    try{await api.removeBillItem?.(selBill.id,itemId);await loadBill(selBill.id);notify('✅ Item removed');}
    catch{notify('❌ Failed to remove item');}
  };

  // ── Payment ─────────────────────────────────────────────────────────────────
  const processPayment=async()=>{
    if(!selBill||!payForm.amount){notify('⚠️ Enter amount');return;}
    setSaving(true);
    try{
      await fetch(`${BASE}/billing/${selBill.id}/payment`,{method:'POST',headers:hdr(),body:JSON.stringify({amount:+(payForm.amount),paymentMethod:payForm.paymentMethod,transactionId:payForm.transactionId||null,remarks:payForm.remarks||null,paymentDate:new Date().toISOString()})});
      notify('✅ Payment recorded!');setShowPayModal(false);
      setPayForm({amount:'',paymentMethod:'CASH',transactionId:'',remarks:''});
      await loadBill(selBill.id);loadBills();
    }catch{notify('❌ Payment failed');}
    setSaving(false);
  };

  // ── Discount ────────────────────────────────────────────────────────────────
  const applyDiscount=async()=>{
    if(!selBill||!discForm.reason){notify('⚠️ Reason required');return;}
    setSaving(true);
    try{
      await api.applyDiscount(selBill.id,{
        discountType:discForm.discountType,
        fixedAmount:discForm.discountType==='FIXED'?+(discForm.fixedAmount||0):null,
        percentage:discForm.discountType==='PERCENTAGE'?+(discForm.percentage||0):null,
        discountValue:discForm.discountType==='FIXED'?+(discForm.fixedAmount||0):Math.round(+(selBill.netAmount||selBill.net_amount||0)*(+(discForm.percentage||0)/100)*100)/100,
        reason:discForm.reason,
      });
      notify('✅ Discount applied!');setShowDiscModal(false);
      setDiscForm({discountType:'FIXED',fixedAmount:'',percentage:'',reason:''});
      await loadBill(selBill.id);loadBills();
    }catch{notify('❌ Discount failed');}
    setSaving(false);
  };

  // ── Refund ──────────────────────────────────────────────────────────────────
  const processRefund=async()=>{
    if(!selBill||!refundForm.refundAmount){notify('⚠️ Enter amount');return;}
    setSaving(true);
    try{
      await fetch(`${BASE}/billing/${selBill.id}/refund`,{method:'POST',headers:hdr(),body:JSON.stringify({refundAmount:+(refundForm.refundAmount),refundMethod:refundForm.refundMethod,refundReason:refundForm.refundReason,refundDate:new Date().toISOString()})});
      notify('✅ Refund processed!');setShowRefundModal(false);
      setRefundForm({refundAmount:'',refundMethod:'CASH',refundReason:''});
      await loadBill(selBill.id);loadBills();
    }catch{notify('❌ Refund failed');}
    setSaving(false);
  };

  // ── IPD Deposit ─────────────────────────────────────────────────────────────
  const collectDeposit=async()=>{
    if(!depositForm.amount){notify('⚠️ Enter amount');return;}
    const admId=(selBill?.admissionId||selBill?.admission_id)||ipdAdmissionId;
    const pid=(selBill?.patientId||selBill?.patient_id)||ipdPatientId;
    if(!admId){notify('⚠️ No admission linked');return;}
    setSaving(true);
    try{
      await fetch(`${BASE}/billing/ipd/deposits`,{method:'POST',headers:hdr(),body:JSON.stringify({admissionId:admId,patientId:pid,amount:+(depositForm.amount),paymentMode:depositForm.paymentMode,referenceNumber:depositForm.referenceNumber||null,notes:depositForm.notes||null})});
      notify('✅ Deposit collected!');setShowDepositModal(false);
      setDepositForm({amount:'',paymentMode:'CASH',referenceNumber:'',notes:''});
      if(selBill)await loadBill(selBill.id);
      if(ipdAdmissionId)await loadIPDBill(ipdAdmissionId);
    }catch{notify('❌ Failed');}
    setSaving(false);
  };

  // ── Insurance claim ─────────────────────────────────────────────────────────
  const createClaim=async()=>{
    if(!selBill||!claimForm.claimType){notify('⚠️ Select claim type');return;}
    setSaving(true);
    try{
      await api.createBillingInsuranceClaim?.({
        billId:selBill.id,patientId:selBill.patientId||selBill.patient_id,
        ...claimForm,claimAmount:+(claimForm.claimAmount||0),patientShare:+(claimForm.patientShare||0),
        submittedAt:new Date().toISOString(),
      });
      notify('✅ Insurance claim created!');setShowClaimModal(false);
      setClaimForm({claimType:'TPA',insuranceCompany:'',policyNumber:'',authorizationNumber:'',claimAmount:'',patientShare:'',remarks:''});
      await loadBill(selBill.id);
    }catch{notify('❌ Failed to create claim');}
    setSaving(false);
  };

  // ── Cash shift ──────────────────────────────────────────────────────────────
  const openShift=async()=>{
    if(!shiftForm.counterName){notify('⚠️ Enter counter name');return;}
    setSaving(true);
    try{
      await api.openCashShift?.({counterName:shiftForm.counterName,openingBalance:+(shiftForm.openingBalance||0)});
      notify('✅ Shift opened!');setShowShiftModal(false);
      const r=await api.getCurrentShift?.();setShift(r?.data||null);
    }catch{notify('❌ Failed');}
    setSaving(false);
  };
  const closeShift=async()=>{
    if(!shift||!window.confirm('Close this shift?'))return;
    setSaving(true);
    try{await api.closeCashShift?.(shift.id,{});notify('✅ Shift closed!');setShift(null);}
    catch{notify('❌ Failed');}
    setSaving(false);
  };

  // ── Print bill ──────────────────────────────────────────────────────────────
  const printBill=async(bill:any,items:any[],payments:any[],deposits:any[])=>{
    const h=await getHospitalInfo();
    const net=+(bill.net_amount||bill.netAmount||0);
    const paid=+(bill.paid_amount||bill.paidAmount||0);
    const bal=+(bill.balance_amount||bill.balanceAmount||0);
    const disc=+(bill.discount_amount||bill.discountAmount||0);
    const gst=+(bill.gst_amount||bill.gstAmount||0);
    const totalDep=deposits.reduce((s:number,d:any)=>s+(+(d.amount||0)),0);
    const rows=items.map((it:any,i:number)=>`<tr><td>${i+1}</td><td><strong>${it.itemDescription||it.item_description||'—'}</strong></td><td class="c">${it.quantity||1}</td><td class="r">${fmtCurrency(+(it.unitPrice||it.unit_price||0))}</td><td class="r">${fmtCurrency((+(it.cgstAmount||0))+(+(it.sgstAmount||0)))}</td><td class="r"><strong>${fmtCurrency(+(it.totalPrice||it.total_price||0))}</strong></td></tr>`).join('');
    const prows=payments.map((p:any)=>`<tr><td>${fmtDT(p.paymentDate||p.payment_date||p.createdAt)}</td><td>${p.paymentMethod||p.payment_method}</td><td>${p.transactionId||p.transaction_id||'—'}</td><td class="r"><strong>${fmtCurrency(+(p.amount||0))}</strong></td></tr>`).join('');
    const html=`<!DOCTYPE html><html><head><title>Bill ${bill.billNumber||bill.bill_number}</title>
    <style>*{box-sizing:border-box;margin:0;padding:0;}body{font-family:Arial,sans-serif;font-size:12px;padding:20px;}
    .hdr{text-align:center;border-bottom:2px solid #02357A;padding-bottom:10px;margin-bottom:14px;}
    .hdr h1{font-size:18px;color:#02357A;font-weight:900;}.hdr p{font-size:10px;color:#666;margin-top:2px;}
    .meta{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:14px;}
    .mb{background:#f8f9fa;border:1px solid #e2e8f0;border-radius:6px;padding:8px;}
    .mb .k{color:#666;font-size:9px;text-transform:uppercase;}.mb .v{font-weight:700;font-size:12px;color:#1e293b;margin-top:2px;}
    table{width:100%;border-collapse:collapse;margin-bottom:10px;font-size:11px;}
    th{background:#02357A;color:#fff;padding:5px 8px;text-align:left;font-size:9px;text-transform:uppercase;}
    td{padding:4px 8px;border-bottom:1px solid #e2e8f0;}.r{text-align:right;}.c{text-align:center;}
    .tot table{width:240px;margin-left:auto;}.tot td{border:none;padding:2px 8px;}
    .grand{font-size:14px;font-weight:900;color:#02357A;border-top:2px solid #02357A;padding-top:4px;}
    .sign{text-align:center;margin-top:30px;}.sl{border-top:1px solid #000;width:140px;margin:0 auto 3px;}
    .ftr{margin-top:16px;border-top:1px solid #e2e8f0;padding-top:8px;display:flex;justify-content:space-between;font-size:9px;color:#94a3b8;}
    @media print{body{padding:8px;}}</style></head><body>
    <div class="hdr"><h1>🏥 ${h.name}</h1><p>${[h.address,h.phone,h.email].filter(Boolean).join(' · ')||'Address · City · Phone · Email'}</p>${h.regNo||h.gstin?`<p style="font-size:9px;color:#888;margin-top:2px;">${[h.regNo?'Reg No: '+h.regNo:'',h.gstin?'KRA PIN: '+h.gstin:''].filter(Boolean).join(' · ')}</p>`:''}<p style="margin-top:5px;font-size:13px;font-weight:700;color:#02357A;">${t('billing.billInvoice','BILL / INVOICE')}</p></div>
    <div class="meta">
      <div class="mb"><div class="k">${t('billing.billNumber','Bill Number')}</div><div class="v">${bill.billNumber||bill.bill_number}</div></div>
      <div class="mb"><div class="k">${t('common.status','Status')}</div><div class="v">${getStatusLabel(bill.status,t)}</div></div>
      <div class="mb"><div class="k">${t('common.patient','Patient')}</div><div class="v">${bill.patient_first_name||''} ${bill.patient_last_name||''}</div><div style="font-size:9px;color:#64748b">UHID: ${bill.uhid||'—'}</div></div>
      <div class="mb"><div class="k">${t('billing.billTypeDate','Bill Type / Date')}</div><div class="v">${getStatusLabel(bill.bill_type||bill.billType,t)}</div><div style="font-size:9px;color:#64748b">${fmtD(bill.bill_date||bill.billDate)}</div></div>
    </div>
    <table><thead><tr><th>#</th><th>${t('common.description','Description')}</th><th class="c">${t('billing.qty','Qty')}</th><th class="r">${t('billing.unitPrice','Unit Price')}</th><th class="r">${t('billing.vat','VAT')}</th><th class="r">${t('common.total','Total')}</th></tr></thead>
    <tbody>${rows||`<tr><td colspan="6" class="c" style="color:#94a3b8;padding:10px">${t('billing.noItems','No items')}</td></tr>`}</tbody></table>
    <div class="tot"><table>
      ${disc>0?`<tr><td style="color:#dc2626">${t('common.discount','Discount')}</td><td class="r" style="color:#dc2626">-${fmtCurrency(disc)}</td></tr>`:''}
      ${gst>0?`<tr><td style="color:#64748b">${t('billing.vat','VAT')}</td><td class="r">${fmtCurrency(gst)}</td></tr>`:''}
      <tr><td class="grand">${t('billing.netAmount','Net Amount')}</td><td class="r grand">${fmtCurrency(net)}</td></tr>
      <tr><td style="color:#16a34a">${t('common.paid','Paid')}</td><td class="r" style="color:#16a34a;font-weight:700">${fmtCurrency(paid)}</td></tr>
      ${totalDep>0?`<tr><td style="color:#2563eb">${t('billing.deposit','Deposit')}</td><td class="r" style="color:#2563eb;font-weight:700">${fmtCurrency(totalDep)}</td></tr>`:''}
      <tr><td style="color:${bal>0?'#dc2626':'#16a34a'};font-weight:700">${t('common.balance','Balance')}</td><td class="r" style="color:${bal>0?'#dc2626':'#16a34a'};font-weight:700">${fmtCurrency(bal)}</td></tr>
    </table></div>
    ${payments.length>0?`<p style="font-weight:700;margin-bottom:5px;font-size:11px">${t('billing.paymentHistory','Payment History')}</p>
    <table><thead><tr><th>${t('common.date','Date')}</th><th>${t('billing.mode','Mode')}</th><th>${t('billing.reference','Reference')}</th><th class="r">${t('common.amount','Amount')}</th></tr></thead><tbody>${prows}</tbody></table>`:''}
    <div class="sign"><div class="sl"></div><p style="font-size:9px">${t('billing.authorisedSignatory','Authorised Signatory')}</p></div>
    <div class="ftr"><span>${t('billing.generated','Generated')}: ${new Date().toLocaleString('en-KE')}</span><span>${t('billing.computerGeneratedBill','Computer generated bill')}</span></div>
    <script>window.onload=()=>{window.print();}<\/script></body></html>`;
    const w=window.open('','_blank','width=900,height=700');
    if(!w)return;w.document.write(html);w.document.close();
  };

  const TABS=[
    ['bills','📋 Bills'],['new','➕ New Bill'],['ipd','🏥 IPD Bill'],
    ['claims','🪪 Insurance'],['counter','💵 Cash Counter'],['reports','📊 Reports'],
  ];

  // ── Render ──────────────────────────────────────────────────────────────────
  return(
    <Layout title="Billing & Payments" actions={
      <div className="flex gap-2">
        {shift&&<div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2"><div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"/><span className="text-xs font-bold text-emerald-700">Shift: {shift.counterName}</span></div>}
        <button onClick={()=>setTab('new')} className={BTN_PRI}>➕ New Bill</button>
      </div>
    }>
      {msg&&<div className={`fixed top-5 right-5 z-50 px-5 py-3 rounded-2xl shadow-xl text-sm font-semibold border ${msg.startsWith('✅')?'bg-green-50 text-green-800 border-green-200':msg.startsWith('⚠️')?'bg-amber-50 text-amber-800 border-amber-200':'bg-red-50 text-red-800 border-red-200'}`}>{msg}</div>}

      {/* Tabs */}
      <div className="flex gap-2 mb-5 overflow-x-auto pb-1 scrollbar-thin">
        {TABS.map(([k,l])=>(
          <button key={k} onClick={()=>setTab(k)}
            className={`px-4 py-2 rounded-xl text-xs font-semibold whitespace-nowrap border-2 flex-shrink-0 transition-all ${tab===k?'bg-[#02357A] text-white border-[#02357A] shadow-lg shadow-[#02357A]/20':'bg-white text-slate-600 border-slate-200 hover:border-[#02357A] hover:text-[#02357A]'}`}>
            {l}
          </button>
        ))}
        {selBill&&(
          <button onClick={()=>setTab('view')}
            className={`px-4 py-2 rounded-xl text-xs font-semibold whitespace-nowrap border-2 flex-shrink-0 transition-all ${tab==='view'?'bg-[#02357A] text-white border-[#02357A] shadow-lg shadow-[#02357A]/20':'bg-white text-slate-600 border-slate-200 hover:border-[#02357A] hover:text-[#02357A]'}`}>
            🧾 {selBill.billNumber||selBill.bill_number}
          </button>
        )}
      </div>

      {/* ══ BILLS LIST ══ */}
      {tab==='bills'&&(
        <div className="space-y-4">
          <div className="flex gap-3 flex-wrap items-center">
            <div className="relative flex-1 min-w-48">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search patient, UHID, bill number..." className={`${INP} pl-10`}/>
            </div>
            <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} className={`${INP} w-36`}>
              <option value="">All Status</option>
              {['DRAFT','UNPAID','PARTIAL','PAID','CANCELLED','REFUNDED'].map(s=><option key={s}>{s}</option>)}
            </select>
            <select value={typeFilter} onChange={e=>setTypeFilter(e.target.value)} className={`${INP} w-36`}>
              <option value="">All Types</option>
              {BILL_TYPES.map(t=><option key={t}>{t}</option>)}
            </select>
            <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} className={`${INP} w-36`} title="From date"/>
            <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} className={`${INP} w-36`} title="To date"/>
            <button onClick={loadBills} className={BTN_SEC}>🔄</button>
          </div>

          <div className={CARD}>
            <div className={CARD_HDR}>
              <span className="font-bold text-slate-800">All Bills</span>
              <span className="text-xs text-slate-400">{bills.length} record(s)</span>
            </div>
            {loading?(
              <div className="p-10 text-center"><div className="w-8 h-8 border-4 border-[#02357A]/20 border-t-[#02357A] rounded-full animate-spin mx-auto"/></div>
            ):bills.length===0?(
              <div className="p-12 text-center"><p className="text-4xl mb-3">🧾</p><p className="text-slate-500 font-medium">No bills found</p></div>
            ):(
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="bg-slate-50 border-b border-slate-100">
                    {['Bill No','Patient','Type','Date','Total','Paid','Balance','Status',''].map(h=>(
                      <th key={h} className={`px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide ${['Total','Paid','Balance'].includes(h)?'text-right':'text-left'}`}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {bills.map((b:any)=>{
                      const net=+(b.net_amount||b.netAmount||b.total_amount||0);
                      const paid=+(b.paid_amount||b.paidAmount||0);
                      const bal=+(b.balance_amount||b.balanceAmount||0);
                      return(
                        <tr key={b.id} className="hover:bg-blue-50/30 cursor-pointer transition-colors" onClick={()=>{loadBill(b.id);setTab('view');}}>
                          <td className="px-4 py-3 font-mono text-xs font-black text-[#02357A]">{b.bill_number||b.billNumber}</td>
                          <td className="px-4 py-3"><p className="font-semibold text-slate-800">{b.patient_first_name||''} {b.patient_last_name||''}</p><p className="text-xs text-slate-400 font-mono">{b.uhid||''}</p></td>
                          <td className="px-4 py-3"><span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-lg font-semibold">{b.bill_type||b.billType}</span></td>
                          <td className="px-4 py-3 text-xs text-slate-500">{fmtD(b.bill_date||b.billDate)}</td>
                          <td className="px-4 py-3 text-right font-bold text-slate-800">{fmtCurrency(net)}</td>
                          <td className="px-4 py-3 text-right text-emerald-600 font-semibold">{fmtCurrency(paid)}</td>
                          <td className={`px-4 py-3 text-right font-bold ${bal>0?'text-red-600':'text-emerald-600'}`}>{fmtCurrency(bal)}</td>
                          <td className="px-4 py-3"><span className={`text-xs px-2.5 py-1 rounded-full font-bold ${STATUS_CSS[b.status]||'bg-slate-100 text-slate-500'}`}>{b.status}</span></td>
                          <td className="px-4 py-3"><button onClick={e=>{e.stopPropagation();loadBill(b.id);setTab('view');}} className="text-xs text-[#02357A] hover:underline font-medium">View →</button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ NEW BILL ══ */}
      {tab==='new'&&(
        <div className="max-w-4xl space-y-5">
          <div className={CARD}>
            <div className={CARD_HDR}><h3 className="font-bold text-slate-800">👤 Patient & Bill Type</h3></div>
            <div className="p-5 space-y-4">
              <div style={{isolation:'isolate',position:'relative'}}>
                <PatientSearchInput label="Patient *" selected={selectedPatient}
                  onSelect={(p:any)=>{setSelectedPatient(p);setBillForm((f:any)=>({...f,patientId:p.id}));}}
                  onClear={()=>{setSelectedPatient(null);setBillForm((f:any)=>({...f,patientId:''}));}}/>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Field label="Bill Type" required>
                  <select value={billForm.billType} onChange={e=>setBillForm((f:any)=>({...f,billType:e.target.value}))} className={INP}>
                    {BILL_TYPES.map(t=><option key={t}>{t}</option>)}
                  </select>
                </Field>
                {billForm.billType==='IPD'&&(
                  <Field label="Admission ID">
                    <input value={billForm.admissionId} onChange={e=>setBillForm((f:any)=>({...f,admissionId:e.target.value}))} placeholder="IPD Admission ID" className={INP}/>
                  </Field>
                )}
                {billForm.billType==='PACKAGE'&&(
                  <Field label="Package">
                    <select value={billForm.packageId||''} onChange={e=>{
                      const pkg=packages.find((p:any)=>p.id===e.target.value);
                      setBillForm((f:any)=>({...f,packageId:e.target.value,
                        items:pkg?[{description:pkg.packageName,serviceId:null,itemCode:pkg.packageCode,quantity:1,unitPrice:+(pkg.packageCost||0),taxPercentage:0,itemCategory:'PACKAGE'}]:f.items,
                      }));
                    }} className={INP}>
                      <option value="">Select Package</option>
                      {packages.map((p:any)=><option key={p.id} value={p.id}>{p.packageName} — {fmtCurrency(+(p.packageCost||0))}</option>)}
                    </select>
                  </Field>
                )}
                <Field label="VAT %">
                  <select value={billForm.gstPercentage} onChange={e=>setBillForm((f:any)=>({...f,gstPercentage:e.target.value}))} className={INP}>
                    {[0,8,16].map(g=><option key={g} value={g}>{g}%</option>)}
                  </select>
                </Field>
                <Field label="Due Date">
                  <input type="date" value={billForm.dueDate} onChange={e=>setBillForm((f:any)=>({...f,dueDate:e.target.value}))} className={INP} min={new Date().toISOString().split('T')[0]}/>
                </Field>
              </div>
              <Field label="Notes">
                <input value={billForm.notes} onChange={e=>setBillForm((f:any)=>({...f,notes:e.target.value}))} placeholder="Optional notes..." className={INP}/>
              </Field>
            </div>
          </div>

          <div className={CARD}>
            <div className={CARD_HDR}>
              <h3 className="font-bold text-slate-800">📋 Bill Items</h3>
              <button onClick={addItem} className="text-xs bg-[#02357A] text-white px-3 py-1.5 rounded-xl hover:bg-[#015CA6] font-semibold">+ Add Item</button>
            </div>
            <div className="p-5 space-y-3">
              {/* Service master search */}
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">🔍</span>
                <input value={serviceSearch} onChange={e=>setServiceSearch(e.target.value)} placeholder="Search service master to add (e.g. Consultation, CBC, X-Ray, Dressing...)" className={`${INP} pl-10`}/>
                {serviceSearch.length>=2&&(
                  <div className="absolute z-[9999] top-full left-0 right-0 mt-1 bg-white border-2 border-slate-200 rounded-2xl shadow-2xl max-h-56 overflow-y-auto">
                    {serviceMaster.filter((s:any)=>(s.serviceName||s.service_name||'').toLowerCase().includes(serviceSearch.toLowerCase())||(s.serviceCode||s.service_code||'').toLowerCase().includes(serviceSearch.toLowerCase())).slice(0,12).map((s:any)=>(
                      <div key={s.id} onClick={()=>{
  const isSHA=!!(s.isSHACovered||s.is_sha_covered);
  const shaRate=+(s.shaApprovedRate||s.sha_approved_rate||0);
  setBillForm((f:any)=>({...f,items:[...f.items,{description:s.serviceName||s.service_name||'',serviceId:s.id,itemCode:s.serviceCode||s.service_code||'',quantity:1,
    unitPrice:isSHA&&shaRate>0?shaRate:+(s.unitPrice||s.unit_price||0),
    taxPercentage:isSHA?0:+(s.taxPercentage||s.tax_percentage||0),
    itemCategory:s.category||'CONSULTATION',
    isSHACovered:isSHA,shaInterventionCode:s.shaInterventionCode||s.sha_intervention_code||null,
    shaApprovedRate:shaRate||null,requiresPreAuth:s.requiresPreAuth||s.requires_pre_auth||false,
    paymentMethod:isSHA?'SHA':'CASH',
  }]}));setServiceSearch('');}}
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 cursor-pointer border-b border-slate-100 last:border-0">
                        <div className="flex-1">
                          <p className="font-semibold text-sm text-slate-800">{s.serviceName||s.service_name}</p>
                          <p className="text-xs text-slate-400">{s.serviceCode||s.service_code} · {s.category} · {s.department||''}</p>
                        </div>
                        <span className="text-sm font-black text-emerald-700 flex-shrink-0">{fmtCurrency(+(s.unitPrice||s.unit_price||0))}</span>
                      </div>
                    ))}
                    {serviceMaster.filter((s:any)=>(s.serviceName||s.service_name||'').toLowerCase().includes(serviceSearch.toLowerCase())).length===0&&(
                      <div className="p-3 text-center text-sm text-slate-400">Not in service master — add manually below</div>
                    )}
                  </div>
                )}
              </div>

              {/* Item rows */}
              <div className="space-y-2">
                {billForm.items.map((it:any,i:number)=>(
                  <div key={i} className="grid grid-cols-12 gap-2 items-end bg-slate-50/50 border border-slate-100 rounded-2xl p-3">
                    <div className="col-span-4"><label className={LBL}>Description *</label><input value={it.description} onChange={e=>updItem(i,'description',e.target.value)} placeholder="Service description" className={INP}/></div>
                    <div className="col-span-2"><label className={LBL}>{t('common.category','Category')}</label><select value={it.itemCategory} onChange={e=>updItem(i,'itemCategory',e.target.value)} className={INP}>{ITEM_CATEGORIES.map(c=><option key={c}>{c}</option>)}</select></div>
                    <div className="col-span-1"><label className={LBL}>Qty</label><input type="number" min={1} value={it.quantity} onChange={e=>updItem(i,'quantity',e.target.value)} className={INP}/></div>
                    <div className="col-span-2"><label className={LBL}>Unit Price ({getCurrencySymbol()})</label><input type="number" min={0} value={it.unitPrice} onChange={e=>updItem(i,'unitPrice',e.target.value)} className={INP}/></div>
                    <div className="col-span-1"><label className={LBL}>VAT %</label><input type="number" min={0} value={it.taxPercentage} onChange={e=>updItem(i,'taxPercentage',e.target.value)} className={INP}/></div>
                    <div className="col-span-1 text-right"><label className={LBL}>{t('common.total','Total')}</label><p className="font-black text-slate-800 text-sm py-2.5">{fmtCurrency((+(it.unitPrice||0))*(+(it.quantity||1)))}</p></div>
                    <div className="col-span-1 text-right">{billForm.items.length>1&&<button onClick={()=>removeItem(i)} className="w-9 h-9 rounded-xl bg-red-50 text-red-500 hover:bg-red-100 text-lg flex items-center justify-center mt-5">✕</button>}</div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="border-t border-slate-200 pt-4">
                <div className="flex items-center gap-3 mb-3 max-w-sm ml-auto">
                  <select value={billForm.discountType} onChange={e=>setBillForm((f:any)=>({...f,discountType:e.target.value}))} className={`${INP} !py-1.5`}><option value="FIXED"> Disc</option><option value="PERCENTAGE">% Disc</option></select>
                  <input type="number" min={0} value={billForm.discountAmount} onChange={e=>setBillForm((f:any)=>({...f,discountAmount:e.target.value}))} className={`${INP} !py-1.5`} placeholder="0"/>
                  <input value={billForm.discountReason} onChange={e=>setBillForm((f:any)=>({...f,discountReason:e.target.value}))} placeholder="Reason" className={`${INP} !py-1.5 flex-1`}/>
                </div>
                <div className="flex flex-col items-end gap-1 text-sm">
                  <div className="flex gap-8 text-slate-500"><span>Sub Total</span><span className="font-semibold text-slate-800">{fmtCurrency(subTotal)}</span></div>
                  {gstAmt>0&&<div className="flex gap-8 text-slate-500"><span>VAT ({billForm.gstPercentage}%)</span><span>{fmtCurrency(gstAmt)}</span></div>}
                  {discAmt>0&&<div className="flex gap-8 text-red-500"><span>{t('common.discount','Discount')}</span><span>-{fmtCurrency(discAmt)}</span></div>}
                  <div className="flex gap-8 text-lg font-black border-t pt-2 mt-1 text-[#02357A]"><span>Net Amount</span><span>{fmtCurrency(netTotal)}</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Insurance */}
          <div className={CARD}>
            <div className={CARD_HDR}>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={billForm.insuranceClaim} onChange={e=>setBillForm((f:any)=>({...f,insuranceClaim:e.target.checked}))} className="w-4 h-4 accent-[#02357A]"/>
                <h3 className="font-bold text-slate-800">🪪 Insurance / TPA Claim</h3>
              </label>
            </div>
            {billForm.insuranceClaim&&(
              <div className="p-5 grid grid-cols-2 gap-4">
                <Field label="TPA ID"><input value={billForm.tpaId} onChange={e=>setBillForm((f:any)=>({...f,tpaId:e.target.value}))} placeholder="Medi-Assist, Vidal..." className={INP}/></Field>
                <Field label="TPA Approval Number"><input value={billForm.tpaApprovalNumber} onChange={e=>setBillForm((f:any)=>({...f,tpaApprovalNumber:e.target.value}))} placeholder="Approval/Pre-auth no." className={INP}/></Field>
                <Field label="TPA Amount ({getCurrencySymbol()})"><input type="number" value={billForm.tpaAmount} onChange={e=>setBillForm((f:any)=>({...f,tpaAmount:e.target.value}))} placeholder="0" className={INP}/></Field>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <button onClick={createBill} disabled={saving||!billForm.patientId||!billForm.items[0].description} className={`${BTN_PRI} flex-1 py-3 text-base font-bold`}>
              {saving?'Creating…':`🧾 Create Bill — ${fmtCurrency(netTotal)}`}
            </button>
            <button onClick={()=>{setBillForm({...DEF_FORM});setSelectedPatient(null);}} className={BTN_SEC}>{t('common.clear','Clear')}</button>
          </div>
        </div>
      )}

      {/* ══ BILL VIEW ══ */}
      {tab==='view'&&selBill&&(()=>{
        const net=+(selBill.net_amount||selBill.netAmount||0);
        const paid=+(selBill.paid_amount||selBill.paidAmount||0);
        const bal=+(selBill.balance_amount||selBill.balanceAmount||0);
        const disc=+(selBill.discount_amount||selBill.discountAmount||0);
        const gst=+(selBill.gst_amount||selBill.gstAmount||0);
        const tpa=+(selBill.tpa_amount||selBill.tpaAmount||0);
        const status=selBill.status;
        const totalDeposit=selBillDeposits.reduce((s:number,d:any)=>s+(+(d.amount||0)),0);
        const adjustedDeposit=selBillDeposits.reduce((s:number,d:any)=>s+(+(d.adjustedAmount||d.adjusted_amount||0)),0);
        return(
          <div className="space-y-5">
            <div className={CARD}>
              <div className="p-5 bg-gradient-to-r from-[#02357A] to-[#015CA6] text-white">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <p className="font-mono font-black text-2xl">{selBill.billNumber||selBill.bill_number}</p>
                    <p className="text-blue-200 text-sm mt-1">{selBill.bill_type||selBill.billType} · {fmtD(selBill.bill_date||selBill.billDate)}</p>
                    <p className="text-white font-semibold mt-2">{selBill.patient_first_name||''} {selBill.patient_last_name||''} <span className="text-blue-200 font-mono text-xs ml-2">{selBill.uhid||''}</span></p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <span className={`text-xs px-3 py-1.5 rounded-full font-black ${STATUS_CSS[status]||'bg-white/20 text-white'}`}>{status}</span>
                    {status!=='PAID'&&status!=='CANCELLED'&&(
                      <>
                        <button onClick={()=>{setPayForm({amount:String(bal),paymentMethod:'CASH',transactionId:'',remarks:''});setShowPayModal(true);}} className="text-xs bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1.5 rounded-xl font-bold">💰 Collect Payment</button>
                        <button onClick={()=>setShowDiscModal(true)} className="text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-xl font-bold">% Discount</button>
                        <button onClick={()=>setShowAddItemModal(true)} className="text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-xl font-bold">+ Add Item</button>
                        {(selBill.admissionId||selBill.admission_id)&&<button onClick={()=>setShowDepositModal(true)} className="text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-xl font-bold">🏦 Deposit</button>}
                        <button onClick={()=>setShowClaimModal(true)} className="text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-xl font-bold">🪪 Insurance Claim</button>
                      </>
                    )}
                    {paid>0&&<button onClick={()=>setShowRefundModal(true)} className="text-xs bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-xl font-bold">↩️ Refund</button>}
                    <button onClick={()=>printBill(selBill,selBillItems,selBillPayments,selBillDeposits)} className="text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-xl font-bold">🖨️ Print</button>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-slate-100">
                {[['Net Amount',''+fmtCurrency(net),'text-slate-800'],['Paid',''+fmtCurrency(paid),'text-emerald-600'],['Balance',''+fmtCurrency(bal),bal>0?'text-red-600':'text-emerald-600'],['Deposits',''+fmtCurrency(totalDeposit),'text-blue-600']].map(([l,v,c])=>(
                  <div key={l as string} className="p-4 text-center">
                    <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide">{l}</p>
                    <p className={`text-xl font-black mt-1 ${c}`}>{v}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div className="lg:col-span-2 space-y-4">
                {/* Bill Items with SHA Split */}
                {(()=>{
                  const shaItems=selBillItems.filter((it:any)=>(it.paymentMethod||it.payment_method)==='SHA'||(it.isSHACovered||it.is_sha_covered));
                  const cashItems=selBillItems.filter((it:any)=>!((it.paymentMethod||it.payment_method)==='SHA'||(it.isSHACovered||it.is_sha_covered))&&!(it.requiresPreAuth||it.requires_pre_auth));
                  const preAuthItems=selBillItems.filter((it:any)=>!((it.paymentMethod||it.payment_method)==='SHA')&&(it.requiresPreAuth||it.requires_pre_auth));
                  const shaTotal=shaItems.reduce((s:number,it:any)=>s+(+(it.shaApprovedRate||it.sha_approved_rate||it.totalPrice||it.total_price||0)),0);
                  const cashTotal=cashItems.reduce((s:number,it:any)=>s+(+(it.totalPrice||it.total_price||0)),0);
                  const preAuthTotal=preAuthItems.reduce((s:number,it:any)=>s+(+(it.totalPrice||it.total_price||0)),0);
                  const hasSHA=shaItems.length>0||preAuthItems.length>0;
                  const patCrId=selBill?.sha_beneficiary_cr_id||selBill?.shaBeneficiaryCrId||'';
                  return(
                <div className={CARD}>
                  <div className={CARD_HDR}>
                    <h3 className="font-bold text-slate-800">📋 Bill Items</h3>
                    <div className="flex items-center gap-2">
                      {hasSHA&&<span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-bold">🛡️ SHA Split</span>}
                      <span className="text-xs text-slate-400">{selBillItems.length} items</span>
                    </div>
                  </div>
                  {/* SHA Section */}
                  {shaItems.length>0&&(
                    <div className="border-b border-teal-100">
                      <div className="px-4 py-2 bg-teal-50 flex items-center justify-between">
                        <span className="text-xs font-bold text-teal-700 uppercase">🛡️ SHA / SHIF Bill</span>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-black text-teal-700">{fmtCurrency(shaTotal)}</span>
                          <button onClick={()=>{
                            const iv=shaItems[0]?.shaInterventionCode||shaItems[0]?.sha_intervention_code||'';
                            const rate=String(shaItems[0]?.shaApprovedRate||shaItems[0]?.sha_approved_rate||shaTotal);
                            const qs=new URLSearchParams({interventionCode:iv,crId:patCrId,shaRate:rate,from:selBill?.billType==='IPD'||selBill?.bill_type==='IPD'?'ipd':'opd'});
                            const adm=selBill?.admissionId||selBill?.admission_id;
                            if(adm)qs.set('admissionId',String(adm));
                            const pid=selBill?.patientId||selBill?.patient_id;
                            if(pid)qs.set('patientId',String(pid));
                            const vis=selBill?.appointmentId||selBill?.appointment_id||selBill?.visitId||selBill?.visit_id;
                            if(vis)qs.set('visitId',String(vis));
                            window.open(`/sha/new-claim?${qs.toString()}`,'_blank');
                          }} className="text-xs bg-teal-600 hover:bg-teal-700 text-white px-3 py-1 rounded-lg font-bold">🛡️ SHA Claim</button>
                        </div>
                      </div>
                      <table className="w-full text-sm">
                        <thead><tr className="bg-teal-50/50 border-b border-teal-100">
                          {['Description','SHA Code','Qty','SHA Rate','Total',''].map(h=><th key={h} className={`px-4 py-2 text-xs font-bold text-teal-600 uppercase ${['Qty','SHA Rate','Total'].includes(h)?'text-right':'text-left'}`}>{h}</th>)}
                        </tr></thead>
                        <tbody className="divide-y divide-teal-50">
                          {shaItems.map((it:any)=>(
                            <tr key={it.id} className="hover:bg-teal-50/30">
                              <td className="px-4 py-2.5"><p className="font-semibold text-slate-800">{it.itemDescription||it.item_description}</p><p className="text-xs text-teal-600 mt-0.5">🛡️ SHA Covered</p></td>
                              <td className="px-4 py-2.5 font-mono text-xs text-teal-700">{it.shaInterventionCode||it.sha_intervention_code||'—'}</td>
                              <td className="px-4 py-2.5 text-right">{it.quantity||1}</td>
                              <td className="px-4 py-2.5 text-right text-teal-700 font-semibold">{fmtCurrency(+(it.shaApprovedRate||it.sha_approved_rate||it.unitPrice||it.unit_price||0))}</td>
                              <td className="px-4 py-2.5 text-right font-bold text-teal-700">{fmtCurrency(+(it.shaApprovedRate||it.sha_approved_rate||it.totalPrice||it.total_price||0))}</td>
                              <td className="px-4 py-2.5">{status!=='PAID'&&status!=='CANCELLED'&&<button onClick={()=>removeItemFromBill(it.id)} className="text-red-400 hover:text-red-600 text-sm">✕</button>}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="border-t border-teal-200 bg-teal-50">
                          <tr><td colSpan={4} className="px-4 py-2 text-right text-xs font-bold text-teal-600">SHA Total — DHA reimburses facility</td><td className="px-4 py-2 text-right font-black text-teal-700">{fmtCurrency(shaTotal)}</td><td/></tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                  {/* Cash Section */}
                  {cashItems.length>0&&(
                    <div className="border-b border-slate-100">
                      {hasSHA&&<div className="px-4 py-2 bg-slate-50 flex items-center justify-between"><span className="text-xs font-bold text-slate-600 uppercase">💵 Cash Bill</span><span className="text-sm font-black text-slate-700">{fmtCurrency(cashTotal)}</span></div>}
                      <table className="w-full text-sm">
                        <thead><tr className="bg-slate-50 border-b border-slate-100">
                          {['Description','Category','Qty','Unit Price','VAT','Total',''].map(h=><th key={h} className={`px-4 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide ${['Qty','Unit Price','VAT','Total'].includes(h)?'text-right':'text-left'}`}>{h}</th>)}
                        </tr></thead>
                        <tbody className="divide-y divide-slate-100">
                          {cashItems.map((it:any)=>(
                            <tr key={it.id} className="hover:bg-slate-50/50">
                              <td className="px-4 py-3"><p className="font-semibold text-slate-800">{it.itemDescription||it.item_description}</p>{(it.itemCode||it.item_code)&&<p className="text-xs text-slate-400 font-mono">{it.itemCode||it.item_code}</p>}</td>
                              <td className="px-4 py-3"><span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-lg">{it.itemCategory||it.item_category}</span></td>
                              <td className="px-4 py-3 text-right">{it.quantity||1}</td>
                              <td className="px-4 py-3 text-right">{fmtCurrency(+(it.unitPrice||it.unit_price||0))}</td>
                              <td className="px-4 py-3 text-right text-xs text-slate-500">{fmtCurrency((+(it.cgstAmount||it.cgst_amount||0))+(+(it.sgstAmount||it.sgst_amount||0)))}</td>
                              <td className="px-4 py-3 text-right font-bold text-slate-800">{fmtCurrency(+(it.totalPrice||it.total_price||0))}</td>
                              <td className="px-4 py-3">{status!=='PAID'&&status!=='CANCELLED'&&<button onClick={()=>removeItemFromBill(it.id)} className="text-red-400 hover:text-red-600 text-sm">✕</button>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {/* Pre-Auth Section */}
                  {preAuthItems.length>0&&(
                    <div className="border-b border-amber-100">
                      <div className="px-4 py-2 bg-amber-50 flex items-center justify-between"><span className="text-xs font-bold text-amber-700 uppercase">⚠️ Pre-Auth Pending</span><span className="text-sm font-black text-amber-700">{fmtCurrency(preAuthTotal)}</span></div>
                      <table className="w-full text-sm"><tbody className="divide-y divide-amber-50">
                        {preAuthItems.map((it:any)=>(
                          <tr key={it.id}><td className="px-4 py-2.5 font-semibold text-slate-800">{it.itemDescription||it.item_description}</td>
                          <td className="px-4 py-2.5 font-mono text-xs text-amber-700">{it.shaInterventionCode||it.sha_intervention_code||'—'}</td>
                          <td className="px-4 py-2.5 text-right font-bold text-amber-700">{fmtCurrency(+(it.totalPrice||it.total_price||0))}</td>
                          <td className="px-4 py-2.5"><span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">⚠️ Pre-Auth Required</span></td>
                          <td className="px-4 py-2.5">{status!=='PAID'&&status!=='CANCELLED'&&<button onClick={()=>removeItemFromBill(it.id)} className="text-red-400 hover:text-red-600 text-sm">✕</button>}</td></tr>
                        ))}
                      </tbody></table>
                    </div>
                  )}
                  {selBillItems.length===0&&<div className="p-8 text-center text-slate-400">No items</div>}
                  {/* Grand Total */}
                  <table className="w-full text-sm"><tfoot className="border-t-2 border-slate-200 bg-slate-50">
                    {hasSHA&&shaTotal>0&&<tr><td colSpan={6} className="px-4 py-2 text-right text-xs text-teal-600 font-semibold">🛡️ SHA Total</td><td className="px-4 py-2 text-right text-teal-700 font-bold">{fmtCurrency(shaTotal)}</td></tr>}
                    {hasSHA&&cashTotal>0&&<tr><td colSpan={6} className="px-4 py-2 text-right text-xs text-slate-500 font-semibold">💵 Cash Total</td><td className="px-4 py-2 text-right text-slate-700 font-bold">{fmtCurrency(cashTotal)}</td></tr>}
                    {disc>0&&<tr><td colSpan={6} className="px-4 py-2 text-right text-sm text-red-600 font-semibold">{t('common.discount','Discount')}</td><td className="px-4 py-2 text-right text-red-600 font-bold">-{fmtCurrency(disc)}</td></tr>}
                    {gst>0&&<tr><td colSpan={6} className="px-4 py-2 text-right text-sm text-slate-500">VAT</td><td className="px-4 py-2 text-right text-slate-600 font-semibold">{fmtCurrency(gst)}</td></tr>}
                    <tr><td colSpan={6} className="px-4 py-3 text-right font-black text-slate-800">Net Amount</td><td className="px-4 py-3 text-right font-black text-[#02357A] text-lg">{fmtCurrency(net)}</td></tr>
                  </tfoot></table>
                </div>
                  );
                })()}
                {/* Insurance claims on this bill */}
                {selBillClaims.length>0&&(
                  <div className={CARD}>
                    <div className={CARD_HDR}><h3 className="font-bold text-slate-800">🪪 Insurance Claims</h3></div>
                    <div className="divide-y divide-slate-100">
                      {selBillClaims.map((c:any)=>(
                        <div key={c.id} className="p-4 flex items-start justify-between">
                          <div>
                            <p className="font-bold text-slate-800">{c.claimNumber||c.claim_number} <span className="text-xs text-slate-400">· {c.claimType||c.claim_type}</span></p>
                            <p className="text-sm text-slate-600 mt-0.5">{c.insuranceCompany||c.insurance_company} · Policy: {c.policyNumber||c.policy_number||'—'}</p>
                            <div className="flex gap-3 text-xs mt-1 flex-wrap">
                              <span>Claimed: <strong className="text-slate-800">{fmtCurrency(+(c.claimAmount||c.claim_amount||0))}</strong></span>
                              {(c.approvedAmount||c.approved_amount)&&<span>Approved: <strong className="text-emerald-700">{fmtCurrency(+(c.approvedAmount||c.approved_amount||0))}</strong></span>}
                              <span>Patient Share: <strong className="text-red-600">{fmtCurrency(+(c.patientShare||c.patient_share||0))}</strong></span>
                            </div>
                          </div>
                          <span className={`text-xs px-2.5 py-1 rounded-full font-bold flex-shrink-0 ${CLAIM_STATUS_CSS[c.status]||'bg-slate-100 text-slate-500'}`}>{c.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                {/* Payments */}
                <div className={CARD}>
                  <div className={CARD_HDR}><h3 className="font-bold text-slate-800">💳 Payments</h3></div>
                  {selBillPayments.length===0?(
                    <div className="p-6 text-center text-slate-400 text-sm">No payments recorded</div>
                  ):(
                    <div className="divide-y divide-slate-100">
                      {selBillPayments.map((p:any,i:number)=>(
                        <div key={i} className="p-4">
                          <div className="flex items-center justify-between">
                            <span className="font-black text-emerald-700">{fmtCurrency(+(p.amount||0))}</span>
                            <span className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-100 px-2 py-0.5 rounded-lg font-semibold">{p.paymentMethod||p.payment_method}</span>
                          </div>
                          {(p.transactionId||p.transaction_id)&&<p className="text-xs text-slate-400 mt-0.5 font-mono">{p.transactionId||p.transaction_id}</p>}
                          <p className="text-xs text-slate-400 mt-0.5">{fmtDT(p.paymentDate||p.payment_date||p.createdAt)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Deposits */}
                {selBillDeposits.length>0&&(
                  <div className={CARD}>
                    <div className={CARD_HDR}><h3 className="font-bold text-slate-800">🏦 IPD Deposits</h3></div>
                    <div className="divide-y divide-slate-100">
                      {selBillDeposits.map((d:any,i:number)=>(
                        <div key={i} className="p-4">
                          <div className="flex items-center justify-between">
                            <span className="font-black text-blue-700">{fmtCurrency(+(d.amount||0))}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${d.status==='ACTIVE'?'bg-blue-100 text-blue-700':d.status==='ADJUSTED'?'bg-emerald-100 text-emerald-700':'bg-red-100 text-red-700'}`}>{d.status}</span>
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5">{d.paymentMode||d.payment_mode} · {d.receiptNumber||d.receipt_number}</p>
                          {(+(d.adjustedAmount||d.adjusted_amount||0))>0&&<p className="text-xs text-emerald-600 mt-0.5">Adjusted: {fmtCurrency(+(d.adjustedAmount||d.adjusted_amount))}</p>}
                        </div>
                      ))}
                      <div className="p-3 bg-slate-50 flex justify-between text-xs font-bold">
                        <span className="text-slate-500">Total Deposit</span><span className="text-blue-700">{fmtCurrency(totalDeposit)}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Summary */}
                <div className={CARD}>
                  <div className={CARD_HDR}><h3 className="font-bold text-slate-800">📊 Summary</h3></div>
                  <div className="p-4 space-y-2 text-sm">
                    {[
                      ['Total',''+fmtCurrency(net),'font-black text-[#02357A] text-base'],
                      ['Paid',''+fmtCurrency(paid),'text-emerald-600 font-bold'],
                      ['Deposit (Unadj.)',''+fmtCurrency(totalDeposit-adjustedDeposit),'text-blue-600 font-semibold'],
                      ['Balance Due',''+fmtCurrency(Math.max(0,bal-(totalDeposit-adjustedDeposit))),bal>0?'text-red-600 font-black text-base':'text-emerald-600 font-black text-base'],
                    ].map(([l,v,c])=>(
                      <div key={l as string} className="flex justify-between items-center">
                        <span className="text-slate-500">{l}</span><span className={c as string}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ══ IPD BILLING ══ */}
      {tab==='ipd'&&(
        <div className="space-y-4">
          {/* Admission Search */}
          {!ipdAdmissionId&&(
            <AdmissionSearchInput onSelect={(a:any)=>{
              setIpdAdmissionId(a.id);
              setIpdPatientId(a.patientId||a.patient_id||a.patient?.id||'');
              setIpdNumber(a.ipdNumber||a.ipd_number||'');
              setIpdActiveSection('billing');
              loadIPDBill(a.id);
            }}/>
          )}

          {/* Loading */}
          {ipdBillLoading&&(
            <div className="bg-white rounded-2xl border p-12 text-center">
              <div className="w-10 h-10 border-4 border-[#02357A]/20 border-t-[#02357A] rounded-full animate-spin mx-auto mb-3"/>
              <p className="text-slate-400 text-sm">Loading IPD data…</p>
            </div>
          )}

          {!ipdBillLoading&&ipdAdmissionId&&(
            <>
              {/* Patient + Admission Header */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-5 bg-gradient-to-r from-[#02357A] to-[#015CA6] text-white">
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-4">
                      {/* Avatar */}
                      <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center text-white font-black text-2xl flex-shrink-0">
                        {(ipdAdmDetail?.patientFirstName||ipdAdmDetail?.patient_first_name||ipdAdmDetail?.patient?.firstName||'P')[0]?.toUpperCase()}
                      </div>
                      <div>
                        <p className="font-black text-xl">
                          {ipdAdmDetail?.patientFirstName||ipdAdmDetail?.patient_first_name||''} {ipdAdmDetail?.patientLastName||ipdAdmDetail?.patient_last_name||''}
                        </p>
                        <div className="flex items-center gap-3 mt-1 flex-wrap text-sm text-blue-200">
                          <span className="font-mono font-bold text-white bg-white/20 px-2 py-0.5 rounded-lg">{ipdNumber||ipdAdmDetail?.ipdNumber||ipdAdmDetail?.ipd_number||'—'}</span>
                          <span>UHID: {ipdAdmDetail?.uhid||'—'}</span>
                          {(ipdAdmDetail?.wardName||ipdAdmDetail?.ward_name)&&<span>Ward: {ipdAdmDetail.wardName||ipdAdmDetail.ward_name}</span>}
                          {(ipdAdmDetail?.bedNumber||ipdAdmDetail?.bed_number)&&<span>Bed: {ipdAdmDetail.bedNumber||ipdAdmDetail.bed_number}</span>}
                        </div>
                        <div className="flex gap-2 mt-1.5 flex-wrap text-xs">
                          {(ipdAdmDetail?.admissionDate||ipdAdmDetail?.admission_date)&&<span className="text-blue-200">Admitted: {fmtD(ipdAdmDetail.admissionDate||ipdAdmDetail.admission_date)}</span>}
                          {(ipdAdmDetail?.admissionType||ipdAdmDetail?.admission_type)&&<span className="bg-white/20 text-white px-2 py-0.5 rounded-full font-semibold">{ipdAdmDetail.admissionType||ipdAdmDetail.admission_type}</span>}
                          <span className={`px-2 py-0.5 rounded-full font-bold ${ipdAdmDetail?.status==='ADMITTED'?'bg-emerald-500':'bg-slate-400'} text-white`}>{ipdAdmDetail?.status||'—'}</span>
                        </div>
                      </div>
                    </div>
                    {/* Action buttons */}
                    <div className="flex gap-2 flex-wrap">
                      {!ipdExistingBill?(
                        <>
                          <button onClick={async()=>{setSaving(true);try{const r=await api.generateInterimBill(ipdAdmissionId);notify('✅ Interim bill generated');if(r?.data?.id){await loadBill(r.data.id);setTab('view');}}catch(e:any){notify('❌ '+(e?.response?.data?.message||'Failed'));}setSaving(false);}}
                            disabled={saving} className="text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-xl font-bold border border-white/20">📄 Interim Bill</button>
                          <button onClick={async()=>{if(!window.confirm('Generate FINAL bill? This locks all charges.'))return;setSaving(true);try{const r=await api.generateFinalBill(ipdAdmissionId);notify('✅ Final bill generated');if(r?.data?.id){await loadBill(r.data.id);setTab('view');}}catch(e:any){notify('❌ '+(e?.response?.data?.message||'Failed'));}setSaving(false);}}
                            disabled={saving} className="text-xs bg-emerald-400 hover:bg-emerald-500 text-white px-3 py-1.5 rounded-xl font-bold">✅ Final Bill</button>
                        </>
                      ):<button onClick={()=>setTab('view')} className="text-xs bg-emerald-400 hover:bg-emerald-500 text-white px-3 py-1.5 rounded-xl font-bold">🧾 View Bill →</button>}
                      <button onClick={()=>setShowDepositModal(true)} className="text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-xl font-bold border border-white/20">🏦 Deposit</button>
                      <button onClick={()=>loadIPDBill(ipdAdmissionId)} className="text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-xl font-bold border border-white/20">🔄</button>
                      <button onClick={()=>{setIpdAdmissionId('');setIpdExistingBill(null);setIpdSummary(null);setIpdCharges([]);setIpdDeposits([]);setIpdOrders([]);setIpdNotes([]);setIpdRounds([]);setIpdAdmDetail(null);}}
                        className="text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-xl font-bold border border-white/20">✕ Change</button>
                    </div>
                  </div>
                </div>

                {/* Billing summary stats */}
                <div className="grid grid-cols-3 md:grid-cols-6 divide-x divide-slate-100 border-t border-slate-100">
                  {[
                    ['Charges',''+fmtCurrency(+(ipdSummary?.totalCharges||ipdSummary?.total_charges||ipdCharges.reduce((s:number,c:any)=>s+(+(c.totalPrice||c.total_price||c.amount||0)),0))),'text-slate-800'],
                    ['Deposits',''+fmtCurrency(ipdDeposits.reduce((s:number,d:any)=>s+(+(d.amount||0)),0)),'text-blue-600'],
                    ['Paid',''+fmtCurrency(+(ipdSummary?.paidAmount||ipdSummary?.paid_amount||0)),'text-emerald-600'],
                    ['Balance',''+fmtCurrency(+(ipdSummary?.balanceAmount||ipdSummary?.balance_amount||0)),(+(ipdSummary?.balanceAmount||ipdSummary?.balance_amount||0))>0?'text-red-600':'text-emerald-600'],
                    ['Orders',ipdOrders.length,'text-amber-600'],
                    ['Notes',ipdNotes.length,'text-purple-600'],
                  ].map(([l,v,c])=>(
                    <div key={l as string} className="p-3 text-center">
                      <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide">{l}</p>
                      <p className={`text-lg font-black mt-0.5 ${c}`}>{v}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Section tabs */}
              <div className="flex gap-2 overflow-x-auto pb-1">
                {[
                  ['billing','💰 Billing & Charges'],
                  ['orders','💊 Doctor Orders ('+ipdOrders.length+')'],
                  ['notes','📝 Clinical Notes ('+ipdNotes.length+')'],
                  ['rounds','📋 Rounds ('+ipdRounds.length+')'],
                  ['deposits','🏦 Deposits ('+ipdDeposits.length+')'],
                ].map(([k,l])=>(
                  <button key={k} onClick={()=>setIpdActiveSection(k)}
                    className={`px-4 py-2 rounded-xl text-xs font-semibold whitespace-nowrap border-2 flex-shrink-0 transition-all ${ipdActiveSection===k?'bg-[#02357A] text-white border-[#02357A]':'bg-white text-slate-600 border-slate-200 hover:border-[#02357A] hover:text-[#02357A]'}`}>
                    {l}
                  </button>
                ))}
              </div>

              {/* ── BILLING SECTION ── */}
              {ipdActiveSection==='billing'&&(
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className={CARD_HDR}>
                    <h3 className="font-bold text-slate-800">📋 Charge Breakdown</h3>
                    <span className="font-black text-[#02357A]">{fmtCurrency(ipdCharges.reduce((s:number,c:any)=>s+(+(c.totalPrice||c.total_price||c.amount||0)),0))}</span>
                  </div>
                  {ipdCharges.length===0?(
                    <div className="p-10 text-center"><p className="text-3xl mb-2">📋</p><p className="text-slate-400 text-sm">No charges yet</p><p className="text-xs text-slate-300 mt-1">Charges auto-add when lab, pharmacy, radiology services are used</p></div>
                  ):(
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="bg-slate-50 border-b border-slate-100">
                          {['Description','Category','Date','Qty','Amount'].map(h=>(
                            <th key={h} className={`px-4 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide ${h==='Amount'?'text-right':'text-left'}`}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody className="divide-y divide-slate-100">
                          {ipdCharges.map((c:any,i:number)=>(
                            <tr key={i} className="hover:bg-slate-50/50">
                              <td className="px-4 py-3 font-medium text-slate-800">{c.itemDescription||c.item_description||c.description||'—'}</td>
                              <td className="px-4 py-3"><span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-lg">{c.itemCategory||c.item_category||c.category||'—'}</span></td>
                              <td className="px-4 py-3 text-xs text-slate-500">{fmtD(c.createdAt||c.created_at||c.date||'')}</td>
                              <td className="px-4 py-3 text-slate-600">{c.quantity||1}</td>
                              <td className="px-4 py-3 text-right font-bold text-slate-800">{fmtCurrency(+(c.totalPrice||c.total_price||c.amount||0))}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="border-t-2 border-slate-200 bg-slate-50">
                          <tr><td colSpan={4} className="px-4 py-3 text-right font-black text-slate-800">{t('common.total','Total')}</td><td className="px-4 py-3 text-right font-black text-[#02357A] text-lg">{fmtCurrency(ipdCharges.reduce((s:number,c:any)=>s+(+(c.totalPrice||c.total_price||c.amount||0)),0))}</td></tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* ── DOCTOR ORDERS SECTION ── */}
              {ipdActiveSection==='orders'&&(
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className={CARD_HDR}><h3 className="font-bold text-slate-800">💊 Doctor Orders</h3><span className="text-xs text-slate-400">{ipdOrders.length} orders</span></div>
                  {ipdOrders.length===0?(
                    <div className="p-10 text-center"><p className="text-3xl mb-2">💊</p><p className="text-slate-400 text-sm">No orders placed yet for this admission</p></div>
                  ):(
                    <div className="divide-y divide-slate-100">
                      {ipdOrders.map((o:any,i:number)=>{
                        const det=typeof o.details==='string'?JSON.parse(o.details||'{}'):o.details||{};
                        const PRIORITY_CSS:Record<string,string>={STAT:'bg-red-100 text-red-700',URGENT:'bg-amber-100 text-amber-700',ROUTINE:'bg-slate-100 text-slate-600'};
                        const STATUS_O:Record<string,string>={ACTIVE:'bg-blue-100 text-[#02357A]',COMPLETED:'bg-emerald-100 text-emerald-700',CANCELLED:'bg-red-100 text-red-500',PENDING:'bg-amber-100 text-amber-700'};
                        return(
                          <div key={i} className="p-4 flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className="font-bold text-sm text-slate-800">{o.orderType||o.order_type}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${PRIORITY_CSS[o.priority]||'bg-slate-100'}`}>{o.priority}</span>
                                {(o.orderNumber||o.order_number)&&<span className="font-mono text-xs text-slate-400">{o.orderNumber||o.order_number}</span>}
                              </div>
                              {det.itemName&&<p className="text-sm font-semibold text-slate-700">{det.itemName}</p>}
                              {(det.dosage||det.route||det.frequency)&&(
                                <p className="text-xs text-slate-500 mt-0.5">{[det.dosage,det.route,det.frequency,det.duration].filter(Boolean).join(' · ')}</p>
                              )}
                              {o.notes&&<p className="text-xs text-slate-400 mt-0.5 italic">{o.notes}</p>}
                              <p className="text-xs text-slate-400 mt-1">{fmtDT(o.createdAt||o.created_at)}</p>
                            </div>
                            <span className={`text-xs px-2.5 py-1 rounded-full font-bold flex-shrink-0 ${STATUS_O[o.status]||'bg-slate-100 text-slate-500'}`}>{o.status}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* ── CLINICAL NOTES SECTION ── */}
              {ipdActiveSection==='notes'&&(
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className={CARD_HDR}><h3 className="font-bold text-slate-800">📝 Clinical Notes</h3><span className="text-xs text-slate-400">{ipdNotes.length} notes</span></div>
                  {ipdNotes.length===0?(
                    <div className="p-10 text-center"><p className="text-3xl mb-2">📝</p><p className="text-slate-400 text-sm">No clinical notes recorded</p></div>
                  ):(
                    <div className="divide-y divide-slate-100">
                      {ipdNotes.map((n:any,i:number)=>(
                        <div key={i} className="p-4">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="text-xs font-bold bg-purple-50 text-purple-700 border border-purple-100 px-2 py-0.5 rounded-full">{n.noteType||'PROGRESS'}</span>
                            {n.shift&&<span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{n.shift}</span>}
                            <span className="text-xs text-slate-400 ml-auto">{fmtDT(n.createdAt||n.created_at)}</span>
                          </div>
                          {(n.subjective||n.objective||n.assessment||n.plan)?(
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              {n.subjective&&<div className="bg-slate-50 rounded-xl p-2"><p className="font-bold text-slate-400 uppercase mb-1">S</p><p className="text-slate-700">{n.subjective}</p></div>}
                              {n.objective&&<div className="bg-slate-50 rounded-xl p-2"><p className="font-bold text-slate-400 uppercase mb-1">O</p><p className="text-slate-700">{n.objective}</p></div>}
                              {n.assessment&&<div className="bg-blue-50 rounded-xl p-2"><p className="font-bold text-blue-400 uppercase mb-1">A</p><p className="text-slate-700">{n.assessment}</p></div>}
                              {n.plan&&<div className="bg-emerald-50 rounded-xl p-2"><p className="font-bold text-emerald-400 uppercase mb-1">P</p><p className="text-slate-700">{n.plan}</p></div>}
                            </div>
                          ):<p className="text-sm text-slate-700">{n.noteText||n.note_text}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── ROUNDS SECTION ── */}
              {ipdActiveSection==='rounds'&&(
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className={CARD_HDR}><h3 className="font-bold text-slate-800">📋 Doctor Rounds</h3><span className="text-xs text-slate-400">{ipdRounds.length} rounds</span></div>
                  {ipdRounds.length===0?(
                    <div className="p-10 text-center"><p className="text-3xl mb-2">📋</p><p className="text-slate-400 text-sm">No rounds recorded</p></div>
                  ):(
                    <div className="divide-y divide-slate-100">
                      {ipdRounds.map((r:any,i:number)=>(
                        <div key={i} className="p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-bold text-slate-500">{fmtDT(r.roundDate||r.round_date||r.createdAt)}</span>
                            {r.doctorName&&<span className="text-xs text-slate-400">· Dr. {r.doctorName}</span>}
                          </div>
                          {r.clinicalFindings&&<p className="text-sm text-slate-700"><strong>Findings:</strong> {r.clinicalFindings||r.clinical_findings}</p>}
                          {r.assessment&&<p className="text-sm text-slate-700 mt-1"><strong>Assessment:</strong> {r.assessment}</p>}
                          {r.plan&&<p className="text-sm text-slate-700 mt-1"><strong>Plan:</strong> {r.plan}</p>}
                          {(r.treatmentChanges||r.treatment_changes)&&<p className="text-sm text-amber-700 bg-amber-50 px-2 py-1 rounded-lg mt-1">🔄 Changes: {r.treatmentChanges||r.treatment_changes}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── DEPOSITS SECTION ── */}
              {ipdActiveSection==='deposits'&&(
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className={CARD_HDR}>
                    <h3 className="font-bold text-slate-800">🏦 Deposits Collected</h3>
                    <div className="flex items-center gap-3">
                      <span className="font-black text-blue-600">{fmtCurrency(ipdDeposits.reduce((s:number,d:any)=>s+(+(d.amount||0)),0))}</span>
                      <button onClick={()=>setShowDepositModal(true)} className="text-xs bg-blue-50 text-[#02357A] border border-blue-100 px-3 py-1.5 rounded-xl hover:bg-blue-100 font-bold">+ Add</button>
                    </div>
                  </div>
                  {ipdDeposits.length===0?(
                    <div className="p-8 text-center"><p className="text-slate-400 text-sm">No deposits yet</p></div>
                  ):(
                    <div className="divide-y divide-slate-100">
                      {ipdDeposits.map((d:any,i:number)=>(
                        <div key={i} className="flex items-center justify-between px-5 py-3.5">
                          <div>
                            <p className="font-black text-slate-800">{fmtCurrency(+(d.amount||0))}</p>
                            <p className="text-xs text-slate-400 mt-0.5">{d.paymentMode||d.payment_mode} · {d.receiptNumber||d.receipt_number||'—'} · {fmtD(d.createdAt||d.created_at)}</p>
                            {(+(d.adjustedAmount||d.adjusted_amount||0))>0&&<p className="text-xs text-emerald-600 mt-0.5 font-medium">Adjusted: {fmtCurrency(+(d.adjustedAmount||d.adjusted_amount))}</p>}
                          </div>
                          <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${d.status==='ACTIVE'?'bg-blue-100 text-blue-700':d.status==='ADJUSTED'?'bg-emerald-100 text-emerald-700':'bg-red-100 text-red-700'}`}>{d.status}</span>
                        </div>
                      ))}
                      <div className="px-5 py-3 bg-blue-50 flex justify-between text-sm font-black border-t border-blue-100">
                        <span className="text-slate-600">Total Deposits</span>
                        <span className="text-blue-700">{fmtCurrency(ipdDeposits.reduce((s:number,d:any)=>s+(+(d.amount||0)),0))}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ══ INSURANCE CLAIMS ══ */}
      {tab==='claims'&&(
        <div className="space-y-4">
          <div className={CARD}>
            <div className={CARD_HDR}>
              <h3 className="font-bold text-slate-800">🪪 Insurance Claims</h3>
              <span className="text-xs text-slate-400">{allClaims.length} claims</span>
            </div>
            {allClaims.length===0?(
              <div className="p-12 text-center"><p className="text-4xl mb-3">🪪</p><p className="text-slate-500">No insurance claims yet. Create claims from the Bill View → Insurance Claim button.</p></div>
            ):(
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="bg-slate-50 border-b border-slate-100">
                    {['Claim No','Type','Company','Policy','Claimed','Approved','Patient Share','Status',''].map(h=>(
                      <th key={h} className={`px-4 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide ${['Claimed','Approved','Patient Share'].includes(h)?'text-right':'text-left'}`}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {allClaims.map((c:any)=>(
                      <tr key={c.id} className="hover:bg-blue-50/30 transition-colors">
                        <td className="px-4 py-3 font-mono text-xs font-black text-[#02357A]">{c.claimNumber||c.claim_number}</td>
                        <td className="px-4 py-3"><span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-lg font-semibold">{c.claimType||c.claim_type}</span></td>
                        <td className="px-4 py-3 text-slate-700">{c.insuranceCompany||c.insurance_company||'—'}</td>
                        <td className="px-4 py-3 text-xs text-slate-500 font-mono">{c.policyNumber||c.policy_number||'—'}</td>
                        <td className="px-4 py-3 text-right font-bold text-slate-800">{fmtCurrency(+(c.claimAmount||c.claim_amount||0))}</td>
                        <td className="px-4 py-3 text-right text-emerald-600 font-semibold">{fmtCurrency(+(c.approvedAmount||c.approved_amount||0))}</td>
                        <td className="px-4 py-3 text-right text-red-600 font-semibold">{fmtCurrency(+(c.patientShare||c.patient_share||0))}</td>
                        <td className="px-4 py-3"><span className={`text-xs px-2.5 py-1 rounded-full font-bold ${CLAIM_STATUS_CSS[c.status]||'bg-slate-100 text-slate-500'}`}>{c.status}</span></td>
                        <td className="px-4 py-3">
                          <select onChange={async e=>{
                            if(!e.target.value)return;
                            await api.updateBillingClaimStatus?.(c.id,{status:e.target.value});
                            notify('✅ Status updated');
                            const r=await api.getBillingInsuranceClaims?.();
                            setAllClaims(r?.data?.claims||r?.data?.data||(Array.isArray(r?.data)?r.data:[]));
                            e.target.value='';
                          }} className={`${INP} !py-1 !text-xs w-32`} defaultValue="">
                            <option value="">Update Status</option>
                            {['SUBMITTED','UNDER_REVIEW','APPROVED','PARTIALLY_APPROVED','REJECTED','SETTLED'].map(s=><option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ CASH COUNTER ══ */}
      {tab==='counter'&&(
        <div className="max-w-xl space-y-5">
          {shift?(
            <div className={CARD}>
              <div className="p-5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1"><div className="w-3 h-3 rounded-full bg-emerald-300 animate-pulse"/><span className="text-sm font-semibold">Shift OPEN</span></div>
                    <p className="font-black text-2xl">{shift.counterName}</p>
                    <p className="text-emerald-200 text-sm mt-1">Opened: {fmtDT(shift.openedAt||shift.opened_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-emerald-200 text-xs">Opening Balance</p>
                    <p className="font-black text-2xl">{fmtCurrency(+(shift.openingBalance||shift.opening_balance||0))}</p>
                  </div>
                </div>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {[['Cash','shift.cashCollected||shift.cash_collected||0'],['Card','shift.cardCollected||shift.card_collected||0'],['UPI','shift.upiCollected||shift.upi_collected||0'],['Total','shift.totalCollected||shift.total_collected||0']].map(([l])=>(
                    <div key={l} className="bg-slate-50 rounded-xl p-3 text-center">
                      <p className="text-xs text-slate-400 font-bold uppercase">{l}</p>
                      <p className="font-black text-slate-800 text-lg">{fmtCurrency(0)}</p>
                    </div>
                  ))}
                </div>
                <button onClick={closeShift} disabled={saving} className={`${BTN_SEC} w-full py-3 font-bold border-red-200 text-red-600 hover:bg-red-50`}>
                  {saving?'Closing…':'🔒 Close Shift'}
                </button>
              </div>
            </div>
          ):(
            <div className={CARD}>
              <div className={CARD_HDR}><h3 className="font-bold text-slate-800">💵 Open Cash Counter Shift</h3></div>
              <div className="p-5 space-y-4">
                <Field label="Counter Name" required>
                  <input value={shiftForm.counterName} onChange={e=>setShiftForm(x=>({...x,counterName:e.target.value}))} placeholder="OPD Counter, IPD Counter, Emergency..." className={INP}/>
                </Field>
                <Field label="Opening Balance ({getCurrencySymbol()})">
                  <input type="number" value={shiftForm.openingBalance} onChange={e=>setShiftForm(x=>({...x,openingBalance:e.target.value}))} placeholder="Cash in drawer" className={INP}/>
                </Field>
                <button onClick={openShift} disabled={saving||!shiftForm.counterName} className={`${BTN_PRI} w-full py-3 font-bold`}>
                  {saving?'Opening…':'🔓 Open Shift'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ REPORTS ══ */}
      {tab==='reports'&&(
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              ['Today Collection',`${fmtCurrency(stats.todayCollection||0)}`,'from-emerald-600 to-teal-600'],
              ['Today Bills',stats.todayBills||0,'from-[#02357A] to-[#015CA6]'],
              ['Pending Amount',`${fmtCurrency(stats.pendingAmount||0)}`,'from-red-500 to-red-600'],
              ['Total Bills',stats.totalBills||0,'from-purple-600 to-indigo-600'],
            ].map(([l,v,g])=>(
              <div key={l as string} className={`rounded-2xl p-5 text-white shadow-lg bg-gradient-to-br ${g}`}>
                <p className="text-xs font-semibold opacity-80 uppercase tracking-wide">{l}</p>
                <p className="text-3xl font-black mt-2">{v}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className={CARD}>
              <div className={CARD_HDR}><h3 className="font-bold text-slate-800">Bill Type Breakdown</h3></div>
              <div className="p-5 grid grid-cols-2 gap-3">
                {(stats.byType||[]).map((t:any)=>(
                  <div key={t.bill_type} className="bg-slate-50 rounded-2xl p-3 text-center border border-slate-100">
                    <p className="text-xs text-slate-400 font-bold uppercase mb-1">{t.bill_type}</p>
                    <p className="font-black text-slate-800 text-lg">{t.count}</p>
                    <p className="text-xs text-[#02357A] font-bold">{fmtCurrency(t.total||0)}</p>
                  </div>
                ))}
                {!(stats.byType?.length)&&<p className="col-span-2 text-slate-400 text-sm text-center py-6">No data yet</p>}
              </div>
            </div>
            <div className={CARD}>
              <div className={CARD_HDR}><h3 className="font-bold text-slate-800">Payment Mode Breakdown</h3></div>
              <div className="p-5 grid grid-cols-2 gap-3">
                {(stats.byPaymentMode||[]).map((p:any)=>(
                  <div key={p.payment_method} className="bg-emerald-50 rounded-2xl p-3 text-center border border-emerald-100">
                    <p className="text-xs text-slate-400 font-bold uppercase mb-1">{p.payment_method}</p>
                    <p className="font-black text-emerald-700 text-lg">{fmtCurrency(p.total||0)}</p>
                  </div>
                ))}
                {!(stats.byPaymentMode?.length)&&<p className="col-span-2 text-slate-400 text-sm text-center py-6">No payments today</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ PAYMENT MODAL ══ */}
      <Modal open={showPayModal} onClose={()=>setShowPayModal(false)} title="💰 Collect Payment">
        <div className="space-y-4">
          {selBill&&<div className="bg-blue-50 rounded-2xl p-4 border border-blue-100 text-sm"><p className="font-bold text-slate-800">{selBill.billNumber||selBill.bill_number}</p><p className="text-slate-500 mt-1">Balance: <span className="font-black text-red-600 text-lg">{fmtCurrency(+(selBill.balance_amount||selBill.balanceAmount||0))}</span></p></div>}
          <Field label="Amount ({getCurrencySymbol()})" required><input type="number" value={payForm.amount} onChange={e=>setPayForm(x=>({...x,amount:e.target.value}))} placeholder="0.00" className={INP}/></Field>
          <Field label="Payment Method" required><select value={payForm.paymentMethod} onChange={e=>setPayForm(x=>({...x,paymentMethod:e.target.value}))} className={INP}>{PAYMENT_METHODS.map(m=><option key={m}>{m}</option>)}</select></Field>
          {payForm.paymentMethod!=='CASH'&&<Field label="Transaction / Reference ID"><input value={payForm.transactionId} onChange={e=>setPayForm(x=>({...x,transactionId:e.target.value}))} placeholder="UPI ref, card last 4, cheque no..." className={INP}/></Field>}
          <Field label="Remarks"><input value={payForm.remarks} onChange={e=>setPayForm(x=>({...x,remarks:e.target.value}))} placeholder="Optional" className={INP}/></Field>
          <div className="flex gap-3 pt-2">
            <button onClick={()=>setShowPayModal(false)} className={BTN_SEC}>{t('common.cancel','Cancel')}</button>
            <button onClick={processPayment} disabled={saving||!payForm.amount} className={`${BTN_PRI} flex-1`}>{saving?'Processing…':'💰 Collect Payment'}</button>
          </div>
        </div>
      </Modal>

      {/* ══ DISCOUNT MODAL ══ */}
      <Modal open={showDiscModal} onClose={()=>setShowDiscModal(false)} title="% Apply Discount">
        <div className="space-y-4">
          <Field label="Discount Type" required><select value={discForm.discountType} onChange={e=>setDiscForm(x=>({...x,discountType:e.target.value}))} className={INP}><option value="FIXED"> Fixed Amount</option><option value="PERCENTAGE">% Percentage</option></select></Field>
          {discForm.discountType==='FIXED'?<Field label="Amount ({getCurrencySymbol()})" required><input type="number" value={discForm.fixedAmount} onChange={e=>setDiscForm(x=>({...x,fixedAmount:e.target.value}))} placeholder="0.00" className={INP}/></Field>:<Field label="Percentage %" required><input type="number" min={0} max={100} value={discForm.percentage} onChange={e=>setDiscForm(x=>({...x,percentage:e.target.value}))} placeholder="0-100" className={INP}/></Field>}
          <Field label="Reason (required for audit)" required><input value={discForm.reason} onChange={e=>setDiscForm(x=>({...x,reason:e.target.value}))} placeholder="Reason for discount..." className={INP}/></Field>
          <div className="flex gap-3 pt-2">
            <button onClick={()=>setShowDiscModal(false)} className={BTN_SEC}>{t('common.cancel','Cancel')}</button>
            <button onClick={applyDiscount} disabled={saving||!discForm.reason} className={`${BTN_PRI} flex-1`}>{saving?'Applying…':'✅ Apply Discount'}</button>
          </div>
        </div>
      </Modal>

      {/* ══ REFUND MODAL ══ */}
      <Modal open={showRefundModal} onClose={()=>setShowRefundModal(false)} title="↩️ Process Refund">
        <div className="space-y-4">
          <Field label="Refund Amount ({getCurrencySymbol()})" required><input type="number" value={refundForm.refundAmount} onChange={e=>setRefundForm(x=>({...x,refundAmount:e.target.value}))} placeholder="0.00" className={INP}/></Field>
          <Field label="Refund Method" required><select value={refundForm.refundMethod} onChange={e=>setRefundForm(x=>({...x,refundMethod:e.target.value}))} className={INP}>{['CASH','UPI','CARD','NET_BANKING','ORIGINAL_PAYMENT_MODE'].map(m=><option key={m}>{m}</option>)}</select></Field>
          <Field label="Reason" required><input value={refundForm.refundReason} onChange={e=>setRefundForm(x=>({...x,refundReason:e.target.value}))} placeholder="Reason for refund..." className={INP}/></Field>
          <div className="flex gap-3 pt-2">
            <button onClick={()=>setShowRefundModal(false)} className={BTN_SEC}>{t('common.cancel','Cancel')}</button>
            <button onClick={processRefund} disabled={saving||!refundForm.refundAmount||!refundForm.refundReason} className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2.5 px-5 rounded-xl transition-all text-sm shadow-md flex-1 disabled:opacity-50">{saving?'Processing…':'↩️ Process Refund'}</button>
          </div>
        </div>
      </Modal>

      {/* ══ DEPOSIT MODAL ══ */}
      <Modal open={showDepositModal} onClose={()=>setShowDepositModal(false)} title="🏦 Collect IPD Deposit">
        <div className="space-y-4">
          <Field label="Deposit Amount ({getCurrencySymbol()})" required><input type="number" value={depositForm.amount} onChange={e=>setDepositForm(x=>({...x,amount:e.target.value}))} placeholder="0.00" className={INP}/></Field>
          <Field label="Payment Mode" required><select value={depositForm.paymentMode} onChange={e=>setDepositForm(x=>({...x,paymentMode:e.target.value}))} className={INP}>{['CASH','CARD','UPI','NET_BANKING','CHEQUE'].map(m=><option key={m}>{m}</option>)}</select></Field>
          <Field label="Reference Number"><input value={depositForm.referenceNumber} onChange={e=>setDepositForm(x=>({...x,referenceNumber:e.target.value}))} placeholder="UPI ref, cheque no, transaction ID" className={INP}/></Field>
          <Field label="Notes"><input value={depositForm.notes} onChange={e=>setDepositForm(x=>({...x,notes:e.target.value}))} placeholder="Optional" className={INP}/></Field>
          <div className="flex gap-3 pt-2">
            <button onClick={()=>setShowDepositModal(false)} className={BTN_SEC}>{t('common.cancel','Cancel')}</button>
            <button onClick={collectDeposit} disabled={saving||!depositForm.amount} className={`${BTN_PRI} flex-1`}>{saving?'Collecting…':'🏦 Collect Deposit'}</button>
          </div>
        </div>
      </Modal>

      {/* ══ INSURANCE CLAIM MODAL ══ */}
      <Modal open={showClaimModal} onClose={()=>setShowClaimModal(false)} title="🪪 Create Insurance Claim" size="lg">
        <div className="space-y-4">
          {selBill&&<div className="bg-blue-50 rounded-2xl p-4 border border-blue-100 text-sm"><p className="font-bold text-slate-800">{selBill.billNumber||selBill.bill_number} · Net: {fmtCurrency(+(selBill.netAmount||selBill.net_amount||0))}</p></div>}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Claim Type" required><select value={claimForm.claimType} onChange={e=>setClaimForm(x=>({...x,claimType:e.target.value}))} className={INP}>{CLAIM_TYPES.map(t=><option key={t}>{t}</option>)}</select></Field>
            <Field label="Insurance Company"><input value={claimForm.insuranceCompany} onChange={e=>setClaimForm(x=>({...x,insuranceCompany:e.target.value}))} placeholder="Jubilee, SHA, NHIF..." className={INP}/></Field>
            <Field label="Policy Number"><input value={claimForm.policyNumber} onChange={e=>setClaimForm(x=>({...x,policyNumber:e.target.value}))} placeholder="Policy no." className={INP}/></Field>
            <Field label="Authorization Number"><input value={claimForm.authorizationNumber} onChange={e=>setClaimForm(x=>({...x,authorizationNumber:e.target.value}))} placeholder="Pre-auth / approval no." className={INP}/></Field>
            <Field label="Claim Amount ({getCurrencySymbol()})" required><input type="number" value={claimForm.claimAmount} onChange={e=>setClaimForm(x=>({...x,claimAmount:e.target.value}))} placeholder="0.00" className={INP}/></Field>
            <Field label="Patient Share ()"><input type="number" value={claimForm.patientShare} onChange={e=>setClaimForm(x=>({...x,patientShare:e.target.value}))} placeholder="Copay / deductible" className={INP}/></Field>
            <Field label="Remarks" col2><input value={claimForm.remarks} onChange={e=>setClaimForm(x=>({...x,remarks:e.target.value}))} placeholder="Additional notes..." className={INP}/></Field>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={()=>setShowClaimModal(false)} className={BTN_SEC}>{t('common.cancel','Cancel')}</button>
            <button onClick={createClaim} disabled={saving||!claimForm.claimAmount} className={`${BTN_PRI} flex-1`}>{saving?'Creating…':'🪪 Submit Insurance Claim'}</button>
          </div>
        </div>
      </Modal>

      {/* ══ ADD ITEM TO EXISTING BILL MODAL ══ */}
      <Modal open={showAddItemModal} onClose={()=>{setShowAddItemModal(false);setAddItemSearch('');}} title="+ Add Item to Bill">
        <div className="space-y-4">
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
            <input value={addItemSearch} onChange={e=>setAddItemSearch(e.target.value)} placeholder="Search service master..." className={`${INP} pl-10`}/>
            {addItemSearch.length>=2&&(
              <div className="absolute z-[9999] top-full left-0 right-0 mt-1 bg-white border-2 border-slate-200 rounded-2xl shadow-2xl max-h-48 overflow-y-auto">
                {serviceMaster.filter((s:any)=>(s.serviceName||s.service_name||'').toLowerCase().includes(addItemSearch.toLowerCase())).slice(0,10).map((s:any)=>(
                  <div key={s.id} onClick={()=>{setAddItemForm({description:s.serviceName||s.service_name||'',serviceId:s.id,itemCode:s.serviceCode||s.service_code||'',itemCategory:s.category||'CONSULTATION',quantity:1,unitPrice:+(s.unitPrice||s.unit_price||0),taxPercentage:+(s.taxPercentage||s.tax_percentage||0)});setAddItemSearch('');}}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 cursor-pointer border-b border-slate-100 last:border-0">
                    <div className="flex-1"><p className="font-semibold text-sm text-slate-800">{s.serviceName||s.service_name}</p><p className="text-xs text-slate-400">{s.serviceCode||s.service_code} · {s.category}</p></div>
                    <span className="font-black text-emerald-700 text-sm">{fmtCurrency(+(s.unitPrice||s.unit_price||0))}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Field label="Description" required><input value={addItemForm.description} onChange={e=>setAddItemForm(x=>({...x,description:e.target.value}))} placeholder="Service description" className={INP}/></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Category"><select value={addItemForm.itemCategory} onChange={e=>setAddItemForm(x=>({...x,itemCategory:e.target.value}))} className={INP}>{ITEM_CATEGORIES.map(c=><option key={c}>{c}</option>)}</select></Field>
            <Field label="Quantity"><input type="number" min={1} value={addItemForm.quantity} onChange={e=>setAddItemForm(x=>({...x,quantity:+e.target.value}))} className={INP}/></Field>
            <Field label="Unit Price ({getCurrencySymbol()})" required><input type="number" min={0} value={addItemForm.unitPrice} onChange={e=>setAddItemForm(x=>({...x,unitPrice:+e.target.value}))} className={INP}/></Field>
            <Field label="VAT %"><input type="number" min={0} value={addItemForm.taxPercentage} onChange={e=>setAddItemForm(x=>({...x,taxPercentage:+e.target.value}))} className={INP}/></Field>
          </div>
          <div className="bg-slate-50 rounded-xl p-3 text-sm flex justify-between font-semibold">
            <span className="text-slate-500">{t('common.total','Total')}</span>
            <span className="text-[#02357A] font-black">{fmtCurrency(addItemForm.unitPrice*addItemForm.quantity)}</span>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={()=>{setShowAddItemModal(false);setAddItemSearch('');}} className={BTN_SEC}>{t('common.cancel','Cancel')}</button>
            <button onClick={addItemToBill} disabled={saving||!addItemForm.description||!addItemForm.unitPrice} className={`${BTN_PRI} flex-1`}>{saving?'Adding…':'+ Add to Bill'}</button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
}
